import { type BaseQueryFn } from '@reduxjs/toolkit/query/react'
import { createApi, retry } from '@reduxjs/toolkit/query/react'
import type { AxiosError, AxiosRequestConfig } from 'axios'
import { isEqual } from 'lodash-es'
import { toast } from 'sonner'
import { parseISO } from 'date-fns'
import axios from 'axios'
import { buildUrl } from '@/utils/common'
import { customAxios } from '@/utils/axiosHelper'
import {
  ServiceErrorHandler,
  type ServicePaginatedResponseType,
  type ServiceCursorPaginatedResponseType,
} from '@/connect-types/backend/service'
import config from '@/config'
import {
  type CreateVenueAccessPoint,
  type RegisterDeviceFromScan,
  type UpdateVenueAccessPoint,
  type VenueAccessPoint,
  type ControllableMediaProtocol,
  type HouseFavourite,
  type MediaAction,
  type MediaDeviceResponse,
  type MediaGroupResponse,
} from '@/pages/[org_id]/venues/[serial]/wi-fi/access-points/schema'
import { type EventLogType } from '@/pages/[org_id]/venues/[serial]/wi-fi/status/event-log'
import { shouldBailout } from '../helpers'
import {
  type WiFiConfigType,
  type AssetReportType,
  type GuestPaymentType,
  type PaymentPlanType,
  type PaymentUpsertType,
  type RadiusGroupPolicyType,
  type SnapConfigType,
  type SnapProvisioningDevice,
  type UniFiConfigType,
  type UniFiControllerType,
  type WiFiAccessPointType,
  type WiFiImpressionsType,
  type WiFiInformType,
  type WiFiSessionType,
  type DetectedHardwareType,
  type WifiCustomerType,
  type WifiVendorType,
  type WifiSessionStatsType,
  type SnapNetworkRuleType,
  type SnapFirewallRuleType,
  type WanSettingsType,
  type WanUplinkMode,
  type CapacityReportType,
  type NetworkOptimisationResponse,
  type DeviceTelemetrySampleType,
  type DeviceBandwidthSampleType,
  type DeviceBandwidthAggregateResponse,
  type DeviceBandwidthTierType,
  type LiveResourceType,
  type LiveQueuesType,
  type LiveApplicationQosType,
  type LiveHotspotActiveType,
  type NetworkScanType,
  type NetworkScanResponseType,
  type DiscoveryFabricStatusType,
  type ExperienceReportType,
  type ExperienceSnapshotType,
  type SnapOnboardingProgress,
  type DhcpNetworkOption,
  type ApplicationQosCatalogItemType,
  type ApplicationQosCustomApplicationType,
  type ApplicationQosCustomApplicationPayload,
  type ApplicationQosPolicyType,
  type ApplicationQosPolicyPayload,
  type UnifiLogCenterResponse,
  type UnifiLogCenterEvent,
  type OrgHardwareDevice,
  type OrgResilienceHealth,
  type OrgResilienceProfile,
  type OrgResilienceSetupOptions,
  type VoiceActivityEventType,
  type VoiceAgentStatusType,
  type VoiceEndpointType,
  type VoiceZoneType,
  type StaffHandsetType,
  type CameraWallTileType,
  type CameraWallType,
  type CameraChannelsResponseType,
} from './types'
import {
  type SystemSchemaType,
  type InterfaceSchemaType,
  type DNSSchemaType,
  type FirewallSchemaType,
  type PoolSchemaType,
  type AddressSchemaType,
  type DHCPServerSchemaType,
  type DHCPServerNetworkSchemaType,
  type HotspotProfileSchemaType,
  type HotspotSchemaType,
  type RadiusSchemaType,
  type WirelessSchemaType,
  type SSLSchemaType,
  type WalledGardenIpSchemaType,
  type FileSchemaType,
  type MikrotikLine,
} from './mikrotik.types'

const axiosBaseQuery =
  (
    { baseUrl }: { baseUrl: string } = { baseUrl: '' }
  ): BaseQueryFn<{
    url: string
    method: AxiosRequestConfig['method']
    data?: AxiosRequestConfig['data']
    params?: AxiosRequestConfig['params']
  }> =>
  async ({ url, method, data, params }) => {
    try {
      const result = await customAxios({
        url: baseUrl + url,
        method,
        data,
        params,
      })
      return { data: result.data }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (method !== 'get' && error.response?.status !== 401) {
          ServiceErrorHandler(error)
        }

        if (shouldBailout(error)) {
          retry.fail(error)
        }

        //
        return {
          error: {
            status: error.response?.status,
            data: error.response?.data || error.message,
          },
        }
      }

      return {
        error: {
          status: undefined,
          data: error instanceof Error ? error.message : String(error),
        },
      }
    }
  }

const ROUTEROS_CONTAINER_RUNTIME_OPT_IN = {
  enableSnapguardRuntime: true,
} as const

interface SquareDeviceType {
  id: string
  attributes?: {
    name?: string
    type?: string
    model?: string
    manufacturer?: string
    locationId?: string
  }
  components?: {
    type?: string
    wifiDetails?: { ipAddressV4?: string; macAddress?: string }
    ethernetDetails?: { ipAddressV4?: string; macAddress?: string }
  }[]
  status?: { category?: string }
  lanDeviceId?: string
  matchedBy?: 'ip' | 'mac'
  lanDevice?: {
    id: string
    name?: string | null
    ip?: string | null
    mac?: string | null
    deviceClass: string
    online: boolean
  }
}

interface SquareDeviceSyncResponse {
  devices: SquareDeviceType[]
  syncedAt: string
  synced: number
  matched: number
  unmatched: number
}

export interface PrinterReceiptTemplate {
  name: string
  width: 32 | 42 | 48
  title?: string
  headerLines: string[]
  footerLines: string[]
  divider: 'dash' | 'equals' | 'stars' | 'none'
  uppercaseTitle: boolean
  showOrderId: boolean
  showPrices: boolean
  showTotal: boolean
  includeNotes: boolean
  orderLabel: string
  totalLabel: string
}

export interface PrinterOrderDraft {
  orderId: string
  title?: string
  lines: {
    name: string
    quantity: number
    price?: string
  }[]
  total?: string
  currency?: string
  notes?: string
  template?: Partial<PrinterReceiptTemplate>
}

export interface PrinterOrderHistory {
  id: string
  external_order_id: string
  job_token?: string | null
  source: 'order_webhook' | 'connect' | 'test'
  state: 'received' | 'queued' | 'fetched' | 'completed' | 'failed'
  preview?: string | null
  order_payload?: PrinterOrderDraft | null
  total?: string | null
  currency?: string | null
  attempt_count: number
  last_error?: string | null
  received_at: string
  queued_at?: string | null
  fetched_at?: string | null
  completed_at?: string | null
  failed_at?: string | null
  events: {
    id: string
    type: string
    status_code?: string | null
    message?: string | null
    preview?: string | null
    created_at: string
  }[]
}

const withRouterOsRuntimeOptIn = <T extends Record<string, unknown>>(
  data?: T
): T & typeof ROUTEROS_CONTAINER_RUNTIME_OPT_IN => ({
  ...(data ?? ({} as T)),
  ...ROUTEROS_CONTAINER_RUNTIME_OPT_IN,
})

const wifi = createApi({
  reducerPath: 'wifi',
  baseQuery: retry(axiosBaseQuery({ baseUrl: config.url.trinity }), {
    maxRetries: 1,
  }),

  refetchOnReconnect: true,
  tagTypes: [
    'WiFiConfig',
    'WiFiInform',
    'UniFiControllers',
    'UniFiLocations',
    'UniFiControllerConfig',
    'PaymentPlans',
    'GuestPayments',
    'NetworkTopology',
    /**
     * Mikrotik tags
     */
    'MikrotikInterfaces',
    'MikrotikSystem',
    'MikrotikStatus',
    'MikrotikHealth',
    'MikrotikHealthCheck',

    'MikrotikRadius',
    'MikrotikHotspot',
    'MikrotikNetwork',
    'MikrotikSsl',
    'MikrotikFiles',
    'MikrotikWalledGarden',
    'MikrotikHosts',
    'MikrotikLines',
    'MikrotikNetworkRules',
    'MikrotikFirewallRules',
    'MikrotikNetworkScan',
    /**
     * Heartbeat
     */
    'NetworkHeartbeat',
    'RadiusPolicyGroups',
    'WanSettings',
    'WanCapacity',
    'ApplicationQos',
    'DeviceTelemetry',
    'DeviceBandwidth',
    'WiFiAccessPoints',
    'WiFiSessions',
    'WiFiGuests',
    'DetectedHardware',
    'SnapDevice',
    'MikrotikTR069',
    'SnapOnboarding',
    'VoiceBridge',
  ],
  endpoints: (build) => ({
    getSettings: build.query<WiFiConfigType, { serial: string }>({
      query: ({ serial }) => ({
        url: `/${serial}/config`,
        method: 'get',
      }),
      providesTags: (res: any) => [{ type: 'WiFiConfig', id: res?.serial }],
      transformResponse: (data: WiFiConfigType) => {
        if (!data.free_questions) {
          data.free_questions = ['Email']
        }
        data.string_type = data.type.toString()
        return data
      },
    }),

    /**
     *
     * asset-reports
     */
    getAssetReports: build.query<
      AssetReportType,
      { orgId: string; hostname: string }
    >({
      query: ({ orgId, hostname }) => ({
        url: `/organisations/${orgId}/health/asset-reports`,
        method: 'get',
        params: {
          hostname,
        },
      }),
    }),
    getTopology: build.query<
      {
        parent_hostname: string | null | undefined
        name: string
        hostname: string
        health_url: string | null | undefined
        status: 'OK' | 'WARNING' | 'ERROR'
        latency: number
        created_at: Date
        updated_at: Date
      }[],
      { serial: string }
    >({
      query: ({ serial }) => ({
        url: `/${serial}/config/hardware/topology`,
        method: 'get',
      }),
      providesTags: (res, _1, { serial }) => [
        { type: 'NetworkTopology', id: 'LIST' },
        { type: 'WiFiAccessPoints', id: 'LIST' },
        { type: 'WiFiSessions', id: 'LIST' },
        { type: 'WiFiConfig', id: serial },
        { type: 'WiFiInform', id: serial },
      ],
    }),
    //@todo -- get types from response, get cursor to populate types
    // where possible
    getEventLogs: build.query<
      ServicePaginatedResponseType<EventLogType>,
      { orgId: string; serial?: string; limit?: number; cursor?: string }
    >({
      query: ({ orgId, serial, limit, cursor }) => ({
        method: 'get',
        url: `/organisations/${orgId}/health/event-logs`,
        params: {
          serial,
          limit,
          cursor,
        },
      }),
      merge: (currentCache, newItems) => {
        currentCache.links = newItems.links
        currentCache.meta = newItems.meta

        newItems.data.forEach((item: any) => {
          const current = currentCache.data.findIndex(
            (it: any) => it?.id === item?.id
          )
          if (current >= 0) {
            currentCache.data[current] = item
          } else {
            currentCache.data.push(item)
          }
        })

        currentCache.data = currentCache.data.sort(
          (a, b) =>
            parseISO(b.created_at).getTime() - parseISO(a.created_at).getTime()
        )
      },
    }),
    getSessions: build.query<
      ServicePaginatedResponseType<WiFiSessionType>,
      {
        serial: string
        data: {
          access_point_id?: string
          org_reg_id?: string
          limit?: number
          cursor?: string
          start?: string
          end?: string
        }
      }
    >({
      query: ({ serial, data }) => ({
        method: 'get',
        url: buildUrl(`/${serial}/guests/sessions`, data),
        data,
      }),
      providesTags: (res: any) => [{ type: 'WiFiSessions', id: 'LIST' }],
    }),

    // Detected-but-unlinked BYO hardware (radius-server pending-external-NAS
    // queue, proxied by trinity). Feature-detected in the UI: a 404 (proxy
    // not deployed yet) is treated as "no data", never an error.
    getDetectedHardware: build.query<
      { data: DetectedHardwareType[] },
      { serial: string; status?: 'pending' | 'matched' | 'ignored' | 'all' }
    >({
      query: ({ serial, status = 'pending' }) => ({
        method: 'get',
        url: buildUrl(`/api/venues/${serial}/detected-hardware`, { status }),
      }),
      providesTags: [{ type: 'DetectedHardware', id: 'LIST' }],
    }),
    updateDetectedHardwareStatus: build.mutation<
      { ok: boolean },
      {
        serial: string
        fingerprint: string
        status: 'pending' | 'matched' | 'ignored'
      }
    >({
      query: ({ serial, fingerprint, status }) => ({
        method: 'PATCH',
        url: `/api/venues/${serial}/detected-hardware/${fingerprint}`,
        data: { status },
      }),
      invalidatesTags: [{ type: 'DetectedHardware', id: 'LIST' }],
    }),

    getLastSessionsByVenue: build.query<
      {
        data: Record<
          string,
          {
            serial: string
            last_session: WiFiSessionType
          }
        >
      },
      {
        orgId: string
      }
    >({
      query: ({ orgId }) => ({
        method: 'get',
        url: `/organisations/${orgId}/guest/last-sessions-by-venue`,
      }),
      providesTags: (res: any) => [{ type: 'WiFiSessions', id: 'LIST' }],
    }),
    getDailyStats: build.query<
      {
        data: {
          date: string
          serial: string
          venue_name: string
          sessions: number
        }[]
      },
      {
        orgId: string
        from: string
        to: string
        serials?: string[]
      }
    >({
      query: ({ orgId, from, to, serials }) => {
        const params: Record<string, any> = { from, to }
        if (serials?.length) {
          params['serials[]'] = serials
        }
        return {
          method: 'get',
          url: `/organisations/${orgId}/guest/daily-stats`,
          params,
        }
      },
      providesTags: [{ type: 'WiFiSessions', id: 'DAILY_STATS' }],
    }),

    /**
     * Aggregated session stats for reporting (Captive Insights) — counts,
     * unique/new guests, per-AP and day/hour series, all computed by trinity.
     * Feature-detected in the UI: a 404 (endpoint not deployed yet) degrades
     * the dependent report sections, never an error.
     */
    getSessionStats: build.query<
      WifiSessionStatsType,
      {
        serial: string
        start: string
        end: string
        granularity?: 'day' | 'hour'
      }
    >({
      query: ({ serial, ...params }) => ({
        method: 'get',
        url: buildUrl(`/${serial}/guests/sessions/stats`, params),
      }),
      providesTags: (_0, _1, { serial }) => [
        { type: 'WiFiSessions', id: `STATS-${serial}` },
      ],
    }),
    getCustomers: build.query<
      ServiceCursorPaginatedResponseType<WifiCustomerType>,
      {
        orgId: string
        serials?: string[]
        limit?: number
        cursor?: string
        sortBy?: 'asc' | 'desc'
      }
    >({
      query: ({ orgId, serials, limit = 50, cursor, sortBy = 'desc' }) => ({
        method: 'get',
        url: buildUrl(`/organisations/${orgId}/guest/people`, {
          limit,
          cursor,
          sort_by: sortBy,
          serials,
        }),
      }),
      providesTags: (res: any, _error, { orgId }) => [
        { type: 'WiFiGuests', id: orgId },
      ],
    }),
    exportSessions: build.mutation<
      void,
      {
        serial: string
        data?: {
          access_point_id?: string
          org_reg_id?: string
          start?: string
          end?: string
          search?: string
        }
      }
    >({
      queryFn: async ({ serial, data }, { signal }) => {
        try {
          const params = new URLSearchParams()
          if (data?.access_point_id)
            params.append('access_point_id', data.access_point_id)
          if (data?.org_reg_id) params.append('org_reg_id', data.org_reg_id)
          if (data?.start) params.append('start', data.start)
          if (data?.end) params.append('end', data.end)
          if (data?.search) params.append('search', data.search)

          const response = await customAxios({
            url: `${config.url.trinity}/${serial}/guests/sessions/export`,
            method: 'get',
            params,
            responseType: 'text',
            signal,
          })

          const csvData = response.data
          const url = window.URL.createObjectURL(new Blob([csvData]))
          const link = document.createElement('a')
          link.href = url
          const filename = `wifi-sessions-${new Date().toISOString().split('T')[0]}.csv`
          link.setAttribute('download', filename)
          document.body.appendChild(link)
          link.click()
          link.remove()

          return { data: undefined }
        } catch (error) {
          if (axios.isAxiosError(error)) {
            return {
              error: {
                status: error.response?.status,
                data: error.response?.data || error.message,
              },
            }
          }
          return { error: { status: 500, data: 'Unknown error' } }
        }
      },
    }),
    getAccessPoint: build.query<
      VenueAccessPoint,
      { serial: string; id: string }
    >({
      query: ({ serial, id }) => ({
        method: 'get',
        url: `/${serial}/config/hardware/access-point/${id}`,
      }),
      providesTags: (res: any) => [{ type: 'WiFiAccessPoints', id: res?.id }],
    }),
    getAccessPoints: build.query<
      ServicePaginatedResponseType<
        WiFiAccessPointType & { _count: { wifi_session_access_point: number } }
      >,
      {
        serial: string
        data: {
          org_reg_id?: string
          limit?: number
          cursor?: string
        }
      }
    >({
      query: ({ serial, data }) => ({
        method: 'get',
        url: buildUrl(`/${serial}/config/hardware/access-point`, data),
        data,
      }),
      providesTags: (res: any) => [{ type: 'WiFiAccessPoints', id: 'LIST' }],
    }),
    createAccessPoint: build.mutation<
      VenueAccessPoint,
      {
        serial: string
        data: CreateVenueAccessPoint
      }
    >({
      query: ({ serial, data }) => ({
        method: 'POST',
        url: buildUrl(`/${serial}/config/hardware/access-point`, data),
        data,
      }),
      invalidatesTags: [{ type: 'WiFiAccessPoints', id: 'LIST' }],
    }),
    registerDeviceFromScan: build.mutation<
      VenueAccessPoint,
      { serial: string; data: RegisterDeviceFromScan }
    >({
      query: ({ serial, data }) => ({
        method: 'POST',
        url: `/${serial}/config/hardware/access-point/from-scan`,
        data,
      }),
      invalidatesTags: [{ type: 'WiFiAccessPoints', id: 'LIST' }],
    }),
    getExposedDevices: build.query<
      { exposed: string[]; deviceSerial: string | null },
      { serial: string }
    >({
      query: ({ serial }) => ({
        method: 'get',
        url: `/${serial}/config/hardware/access-point/exposed`,
      }),
      providesTags: [{ type: 'WiFiAccessPoints', id: 'EXPOSED' }],
    }),
    getDeviceProxySession: build.query<
      { success: true; baseUrl: string; expiresAt: string },
      {
        serial: string
        id: string
        scheme: 'http' | 'https'
        port?: number
      }
    >({
      query: ({ serial, id, ...data }) => ({
        method: 'POST',
        url: `/${serial}/config/hardware/access-point/${id}/proxy-session`,
        data,
      }),
      keepUnusedDataFor: 60,
    }),
    getCameraStreamSession: build.query<
      {
        success: true
        streamUrl: string
        snapshotUrl: string
        expiresAt: string
      },
      { serial: string; id: string }
    >({
      query: ({ serial, id }) => ({
        method: 'POST',
        url: `/${serial}/config/hardware/access-point/${id}/camera-session`,
      }),
      keepUnusedDataFor: 60,
      providesTags: (_result, _error, { id }) => [
        { type: 'WiFiAccessPoints', id: `CAMERA_SESSION_${id}` },
      ],
    }),
    autoConfigureCamera: build.mutation<
      {
        device: VenueAccessPoint
        verification: {
          source: 'configured' | 'vendor-default' | 'onvif'
          label: string
          attempted: number
          verifiedAt: string
        }
      },
      {
        serial: string
        id: string
        host?: string
        channel?: number
      }
    >({
      query: ({ serial, id, ...data }) => ({
        method: 'POST',
        url: `/${serial}/config/hardware/access-point/${id}/camera-auto-configure`,
        data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'WiFiAccessPoints', id },
        { type: 'WiFiAccessPoints', id: 'LIST' },
        { type: 'WiFiAccessPoints', id: 'EXPOSED' },
        { type: 'WiFiAccessPoints', id: `CAMERA_SESSION_${id}` },
      ],
    }),
    saveCameraProfile: build.mutation<
      VenueAccessPoint,
      {
        serial: string
        id: string
        host: string
        scheme: 'rtsp' | 'rtspx'
        path: string
        port: number
        channel?: number
        profileId: string
        streamProfile: 'main' | 'sub'
      }
    >({
      query: ({ serial, id, ...data }) => ({
        method: 'PUT',
        url: `/${serial}/config/hardware/access-point/${id}/camera-profile`,
        data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'WiFiAccessPoints', id },
        { type: 'WiFiAccessPoints', id: 'LIST' },
        { type: 'WiFiAccessPoints', id: `CAMERA_SESSION_${id}` },
      ],
    }),
    getPrinterCloudPrntSetup: build.mutation<
      {
        delivery: 'Star CloudPRNT HTTP' | 'Epson ePOS-Print'
        url?: string
        username?: string
        password?: string
        protocol?: 'Star CloudPRNT HTTP'
        setupOnce?: true
        orderWebhook: {
          url: string
          secret: string
          signatureHeader: 'X-Stampede-Signature'
          signatureFormat: string
        }
        printer: { id: string; name?: string | null; model?: string | null }
      },
      { serial: string; id: string }
    >({
      query: ({ serial, id }) => ({
        method: 'POST',
        url: `/${serial}/config/hardware/access-point/${id}/cloudprnt-setup`,
      }),
    }),
    provisionPrinterCloudPrnt: build.mutation<
      {
        outcome: 'configured' | 'manual-required'
        profile: 'star-webconfig-form' | 'unrecognised-webconfig'
        message: string
        submittedPath?: string
        restartRequested: boolean
        printBridge: true
        printer: { id: string; name?: string | null; model?: string | null }
      },
      {
        serial: string
        id: string
        credentials?: { username: string; password: string }
      }
    >({
      query: ({ serial, id, credentials }) => ({
        method: 'POST',
        url: `/${serial}/config/hardware/access-point/${id}/cloudprnt-provision`,
        data: credentials ? { credentials } : {},
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'WiFiAccessPoints', id },
        { type: 'WiFiAccessPoints', id: 'LIST' },
        { type: 'WiFiAccessPoints', id: `PRINTER-${id}` },
      ],
    }),
    getPrinterStatus: build.query<
      {
        success: true
        printer: { id: string; name?: string | null; model?: string | null }
        agentOnline: boolean
        queuedJobs: number
        status: {
          printerId: string
          printerMAC?: string
          uniqueID?: string
          status?: string
          statusCode?: string
          printingInProgress: boolean
          lastSeenAt: string
          capabilities: {
            encodings?: string[]
            pollingIntervalSeconds?: number
          }
          peripherals: {
            barcodeReaders: number
            keyboards: number
            displays: number
          }
        } | null
        recentOperations: {
          id: string
          sequence: number
          printerId: string
          type:
            | 'order_received'
            | 'connected'
            | 'status'
            | 'queued'
            | 'fetched'
            | 'completed'
            | 'failed'
            | 'template_updated'
          at: string
          jobToken?: string
          orderId?: string
          source?: 'order' | 'test'
          preview?: string
          statusCode?: string
          message?: string
        }[]
        template: PrinterReceiptTemplate
        integration: {
          id: string
          transport: 'star-cloudprnt' | 'epson-epos'
          last_seen_at?: string | null
          last_status?: string | null
          last_status_code?: string | null
          last_error?: string | null
        } | null
        orders: PrinterOrderHistory[]
      },
      { serial: string; id: string }
    >({
      query: ({ serial, id }) => ({
        method: 'GET',
        url: `/${serial}/config/hardware/access-point/${id}/printer/status`,
      }),
      providesTags: (_result, _error, { id }) => [
        { type: 'WiFiAccessPoints', id: `PRINTER-${id}` },
      ],
    }),
    getPrinterTemplate: build.query<
      {
        printerId: string
        agentOnline: boolean
        template: PrinterReceiptTemplate
      },
      { serial: string; id: string }
    >({
      query: ({ serial, id }) => ({
        method: 'GET',
        url: `/${serial}/config/hardware/access-point/${id}/printer/template`,
      }),
      providesTags: (_result, _error, { id }) => [
        { type: 'WiFiAccessPoints', id: `PRINTER-${id}` },
      ],
    }),
    updatePrinterTemplate: build.mutation<
      { printerId: string; template: PrinterReceiptTemplate },
      { serial: string; id: string; template: PrinterReceiptTemplate }
    >({
      query: ({ serial, id, template }) => ({
        method: 'PUT',
        url: `/${serial}/config/hardware/access-point/${id}/printer/template`,
        data: template,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'WiFiAccessPoints', id: `PRINTER-${id}` },
      ],
    }),
    pushPrinterOrder: build.mutation<
      {
        accepted: true
        orderId: string
        externalOrderId: string
        jobToken: string
        state: string
        preview?: string
      },
      { serial: string; id: string; order: PrinterOrderDraft }
    >({
      query: ({ serial, id, order }) => ({
        method: 'POST',
        url: `/${serial}/config/hardware/access-point/${id}/printer/orders`,
        data: order,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'WiFiAccessPoints', id: `PRINTER-${id}` },
      ],
    }),
    printPrinterTest: build.mutation<
      { accepted: true; jobToken: string },
      { serial: string; id: string }
    >({
      query: ({ serial, id }) => ({
        method: 'POST',
        url: `/${serial}/config/hardware/access-point/${id}/printer/test`,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'WiFiAccessPoints', id: `PRINTER-${id}` },
      ],
    }),
    getSquareDevices: build.query<
      { devices: SquareDeviceType[] },
      { serial: string }
    >({
      query: ({ serial }) => ({
        method: 'get',
        url: `/${serial}/config/hardware/access-point/square-devices`,
      }),
      providesTags: [{ type: 'WiFiAccessPoints', id: 'SQUARE' }],
    }),
    syncSquareDevices: build.mutation<
      SquareDeviceSyncResponse,
      { serial: string }
    >({
      query: ({ serial }) => ({
        method: 'post',
        url: `/${serial}/config/hardware/access-point/square-devices/sync`,
      }),
      invalidatesTags: (result) => [
        { type: 'WiFiAccessPoints', id: 'SQUARE' },
        { type: 'WiFiAccessPoints', id: 'LIST' },
        ...(result?.devices.flatMap((device) =>
          device.lanDeviceId
            ? [{ type: 'WiFiAccessPoints' as const, id: device.lanDeviceId }]
            : []
        ) ?? []),
      ],
    }),
    getMediaDeviceStatus: build.query<
      MediaDeviceResponse,
      { serial: string; id: string; protocol: ControllableMediaProtocol }
    >({
      query: ({ serial, id, protocol }) => ({
        method: 'get',
        url: `/${serial}/config/hardware/access-point/${id}/media/status`,
        params: { protocol },
      }),
      providesTags: (_result, _error, { id }) => [
        { type: 'WiFiAccessPoints', id: `MEDIA-${id}` },
      ],
    }),
    autoConfigureMediaDevice: build.mutation<
      MediaDeviceResponse & {
        device: WiFiAccessPointType
        status?: 'available' | 'needs_setup'
        verification: { verifiedAt: string; attempted: number }
      },
      { serial: string; id: string }
    >({
      query: ({ serial, id }) => ({
        method: 'POST',
        url: `/${serial}/config/hardware/access-point/${id}/media-auto-configure`,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'WiFiAccessPoints', id },
        { type: 'WiFiAccessPoints', id: 'LIST' },
        { type: 'WiFiAccessPoints', id: 'EXPOSED' },
        { type: 'WiFiAccessPoints', id: `MEDIA-${id}` },
      ],
    }),
    startMediaReceiverPairing: build.mutation<
      {
        success: true
        complete: boolean
        protocol: 'appletv' | 'samsung-tv'
        pairingId?: string
        deviceProvidesPin?: boolean
        pinToEnter?: number | null
      },
      {
        serial: string
        id: string
        protocol: 'appletv' | 'samsung-tv'
      }
    >({
      query: ({ serial, id, protocol }) => ({
        method: 'POST',
        url: `/${serial}/config/hardware/access-point/${id}/media/pair/start`,
        data: { protocol },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'WiFiAccessPoints', id },
        { type: 'WiFiAccessPoints', id: `MEDIA-${id}` },
      ],
    }),
    finishMediaReceiverPairing: build.mutation<
      { success: true; complete: true },
      { serial: string; id: string; pairingId: string; pin?: string }
    >({
      query: ({ serial, id, ...data }) => ({
        method: 'POST',
        url: `/${serial}/config/hardware/access-point/${id}/media/pair/finish`,
        data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'WiFiAccessPoints', id },
        { type: 'WiFiAccessPoints', id: 'LIST' },
        { type: 'WiFiAccessPoints', id: `MEDIA-${id}` },
      ],
    }),
    getDiscoveryFabricStatus: build.mutation<
      { status: DiscoveryFabricStatusType },
      { serial: string }
    >({
      query: ({ serial }) => ({
        method: 'POST',
        url: `/${serial}/config/hardware/access-point/discovery/status`,
      }),
    }),
    configureDiscoveryFabric: build.mutation<
      { status: DiscoveryFabricStatusType },
      { serial: string; interfaces: string[] }
    >({
      query: ({ serial, interfaces }) => ({
        method: 'PUT',
        url: `/${serial}/config/hardware/access-point/discovery`,
        data: { interfaces },
      }),
    }),
    getHouseFavourites: build.query<
      { favourites: HouseFavourite[] },
      { serial: string }
    >({
      query: ({ serial }) => ({
        method: 'get',
        url: `/${serial}/config/hardware/access-point/media/house-favourites`,
      }),
    }),
    controlMediaDevice: build.mutation<
      MediaDeviceResponse,
      {
        serial: string
        id: string
        protocol: ControllableMediaProtocol
        action: MediaAction
        uri?: string
        favouriteId?: string
        volume?: number
        positionSeconds?: number
        playMode?: 'normal' | 'repeat-one' | 'repeat-all' | 'shuffle'
        appId?: string
        key?: string
        inputId?: string
        metadata?: {
          title?: string
          artist?: string
          album?: string
          artworkUrl?: string
          durationSeconds?: number
        }
      }
    >({
      query: ({ serial, id, ...data }) => ({
        method: 'POST',
        url: `/${serial}/config/hardware/access-point/${id}/media/control`,
        data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'WiFiAccessPoints', id: `MEDIA-${id}` },
      ],
    }),
    controlMediaGroup: build.mutation<
      MediaGroupResponse,
      {
        serial: string
        ids: string[]
        action: MediaAction
        uri?: string
        favouriteId?: string
        volume?: number
        positionSeconds?: number
        playMode?: 'normal' | 'repeat-one' | 'repeat-all' | 'shuffle'
        metadata?: {
          title?: string
          artist?: string
          album?: string
          artworkUrl?: string
          durationSeconds?: number
        }
      }
    >({
      query: ({ serial, ...data }) => ({
        method: 'POST',
        url: `/${serial}/config/hardware/access-point/media/group/control`,
        data,
      }),
      invalidatesTags: [{ type: 'WiFiAccessPoints', id: 'LIST' }],
    }),
    configureMediaGroup: build.mutation<
      { success: boolean; grouped: boolean },
      {
        serial: string
        coordinatorId: string
        memberId: string
        grouped: boolean
      }
    >({
      query: ({ serial, ...data }) => ({
        method: 'POST',
        url: `/${serial}/config/hardware/access-point/media/group`,
        data,
      }),
    }),
    saveMediaGroupSelection: build.mutation<
      {
        success: true
        coordinatorId: string
        memberIds: string[]
        mode: 'native-sonos' | 'coordinated-fanout'
      },
      {
        serial: string
        id: string
        memberIds: string[]
        name?: string
      }
    >({
      query: ({ serial, id, ...data }) => ({
        method: 'PUT',
        url: `/${serial}/config/hardware/access-point/${id}/media/group-selection`,
        data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'WiFiAccessPoints', id },
        { type: 'WiFiAccessPoints', id: 'LIST' },
      ],
    }),
    exposeDevice: build.mutation<
      { ip: string; exposed: string[] },
      { serial: string; id: string }
    >({
      query: ({ serial, id }) => ({
        method: 'POST',
        url: `/${serial}/config/hardware/access-point/${id}/expose`,
      }),
      invalidatesTags: [{ type: 'WiFiAccessPoints', id: 'EXPOSED' }],
    }),
    unexposeDevice: build.mutation<
      { ip: string; exposed: string[] },
      { serial: string; id: string }
    >({
      query: ({ serial, id }) => ({
        method: 'POST',
        url: `/${serial}/config/hardware/access-point/${id}/unexpose`,
      }),
      invalidatesTags: [{ type: 'WiFiAccessPoints', id: 'EXPOSED' }],
    }),
    updateAccessPoint: build.mutation<
      VenueAccessPoint,
      { serial: string; data: UpdateVenueAccessPoint }
    >({
      query: ({ serial, data }) => ({
        method: 'PUT',
        url: buildUrl(
          `/${serial}/config/hardware/access-point/${data.id}`,
          data
        ),
        data,
      }),
      invalidatesTags: (res: any) => [
        { type: 'WiFiAccessPoints', id: 'LIST' },
        { type: 'WiFiSessions', id: 'LIST' },
        { type: 'WiFiAccessPoints', id: res?.id },
      ],
    }),
    deleteAccessPoint: build.mutation<unknown, { serial: string; id: string }>({
      query: ({ serial, id }) => ({
        method: 'DELETE',
        url: `/${serial}/config/hardware/access-point/${id}`,
      }),
      invalidatesTags: [{ type: 'WiFiAccessPoints', id: 'LIST' }],
    }),
    getVoiceEndpoints: build.query<
      { success: true; endpoints: VoiceEndpointType[] },
      { serial: string }
    >({
      query: ({ serial }) => ({
        method: 'get',
        url: `/${serial}/config/hardware/voice/endpoints`,
      }),
      providesTags: (_result, _error, { serial }) => [
        { type: 'VoiceBridge', id: `ENDPOINTS-${serial}` },
      ],
    }),
    // Zero-config handset status read straight off the SnapGuard (works before
    // the PBX is deployed): which DHCP leases are handsets and whether each has
    // a provisioning config waiting.
    getStaffHandsets: build.query<
      { success: true; handsets: StaffHandsetType[] },
      { serial: string }
    >({
      query: ({ serial }) => ({
        method: 'get',
        url: `/${serial}/config/hardware/voice/handsets`,
      }),
      providesTags: (_result, _error, { serial }) => [
        { type: 'VoiceBridge', id: `HANDSETS-${serial}` },
      ],
    }),
    getVoiceZones: build.query<
      { success: true; zones: VoiceZoneType[] },
      { serial: string }
    >({
      query: ({ serial }) => ({
        method: 'get',
        url: `/${serial}/config/hardware/voice/zones`,
      }),
      providesTags: (_result, _error, { serial }) => [
        { type: 'VoiceBridge', id: `ZONES-${serial}` },
      ],
    }),
    getVoiceAgentStatus: build.query<
      { success: true } & VoiceAgentStatusType,
      { serial: string }
    >({
      query: ({ serial }) => ({
        method: 'get',
        url: `/${serial}/config/hardware/voice/agent-status`,
      }),
      providesTags: (_result, _error, { serial }) => [
        { type: 'VoiceBridge', id: `AGENT-${serial}` },
      ],
    }),
    getVoiceActivity: build.query<
      { success: true; events: VoiceActivityEventType[] },
      { serial: string }
    >({
      query: ({ serial }) => ({
        method: 'get',
        url: `/${serial}/config/hardware/voice/activity`,
      }),
      providesTags: (_result, _error, { serial }) => [
        { type: 'VoiceBridge', id: `ACTIVITY-${serial}` },
      ],
    }),
    provisionVoiceEndpoint: build.mutation<
      {
        success: true
        headset: VoiceEndpointType & { secret: string }
        sip?: { domain: string; port: number; transport: string }
      },
      { serial: string; label: string; zone: string }
    >({
      query: ({ serial, ...data }) => ({
        method: 'post',
        url: `/${serial}/config/hardware/voice/endpoints`,
        data,
      }),
      invalidatesTags: (_result, _error, { serial }) => [
        { type: 'VoiceBridge', id: `ENDPOINTS-${serial}` },
        { type: 'VoiceBridge', id: `ZONES-${serial}` },
        { type: 'VoiceBridge', id: `ACTIVITY-${serial}` },
      ],
    }),
    removeVoiceEndpoint: build.mutation<
      { success: true },
      { serial: string; id: string }
    >({
      query: ({ serial, id }) => ({
        method: 'DELETE',
        url: `/${serial}/config/hardware/voice/endpoints/${id}`,
      }),
      // Removing the last headset in a zone removes the zone with it, so the
      // zone list has to refetch alongside the endpoints.
      invalidatesTags: (_result, _error, { serial }) => [
        { type: 'VoiceBridge', id: `ENDPOINTS-${serial}` },
        { type: 'VoiceBridge', id: `ZONES-${serial}` },
        { type: 'VoiceBridge', id: `ACTIVITY-${serial}` },
      ],
    }),
    pageVoiceZone: build.mutation<
      { success: true; paged: number; failed: string[] },
      {
        serial: string
        zone?: string
        extensions?: string[]
        text?: string
        streamUrl?: string
      }
    >({
      query: ({ serial, ...data }) => ({
        method: 'POST',
        url: `/${serial}/config/hardware/voice/page`,
        data,
      }),
      // A page wakes every headset it reaches, so last-seen and the agent's
      // session count both move.
      invalidatesTags: (_result, _error, { serial }) => [
        { type: 'VoiceBridge', id: `ENDPOINTS-${serial}` },
        { type: 'VoiceBridge', id: `AGENT-${serial}` },
        { type: 'VoiceBridge', id: `ACTIVITY-${serial}` },
      ],
    }),
    announceVoiceEndpoint: build.mutation<
      { success: true },
      { serial: string; extension: string; text?: string; streamUrl?: string }
    >({
      query: ({ serial, ...data }) => ({
        method: 'POST',
        url: `/${serial}/config/hardware/voice/announce`,
        data,
      }),
      invalidatesTags: (_result, _error, { serial }) => [
        { type: 'VoiceBridge', id: `ENDPOINTS-${serial}` },
        { type: 'VoiceBridge', id: `AGENT-${serial}` },
        { type: 'VoiceBridge', id: `ACTIVITY-${serial}` },
      ],
    }),
    createCameraWall: build.mutation<
      { success: true } & CameraWallType,
      { serial: string } & (
        | {
            wallId: string
            layout: '2x2' | '3x3'
            accessPointIds: string[]
            fps?: number
          }
        | {
            wallId: string
            layout: 'freeform'
            columns: number
            rows: number
            cellWidth?: number
            cellHeight?: number
            tiles: CameraWallTileType[]
            fps?: number
          }
      )
    >({
      query: ({ serial, ...data }) => ({
        method: 'POST',
        url: `/${serial}/config/hardware/access-point/camera/wall`,
        data,
      }),
      // A wall composites the cameras' existing sessions into a new capability
      // of its own — it leaves the per-camera sessions in place, so no cached
      // tag goes stale.
    }),

    // Enumerate a multi-channel recorder's inputs so each becomes its own
    // camera source. Read-only against the device; nothing is persisted here.
    enumerateCameraChannels: build.mutation<
      CameraChannelsResponseType,
      { serial: string; id: string; host?: string }
    >({
      query: ({ serial, id, ...data }) => ({
        method: 'POST',
        url: `/${serial}/config/hardware/access-point/${id}/camera-channels`,
        data,
      }),
    }),

    getImpressions: build.query<
      ServicePaginatedResponseType<WiFiImpressionsType>,
      {
        serial: string
        query: {
          sort_by?: 'asc' | 'desc'
          limit: number
          cursor: string
          org_reg_id?: string
          access_point_id?: string
        }
      }
    >({
      query: ({ serial, query }) => ({
        method: 'get',
        url: buildUrl(`/${serial}/guests/impressions`, query),
      }),
      serializeQueryArgs: ({ queryArgs }) => {
        return JSON.stringify({
          serial: queryArgs.serial,
          params: {
            ...queryArgs.query,
            cursor: '',
          },
        })
      },
      merge: (currentCache, newItems) => {
        currentCache.links = newItems.links
        currentCache.meta = newItems.meta

        newItems.data.forEach((item: any) => {
          const current = currentCache.data.findIndex(
            (it: any) => it?.id === item?.id
          )
          if (current >= 0) {
            currentCache.data[current] = item
          } else {
            currentCache.data.push(item)
          }
        })

        currentCache.data = currentCache.data.sort(
          (a, b) =>
            parseISO(b.created_at).getTime() - parseISO(a.created_at).getTime()
        )
      },
      forceRefetch({ currentArg, previousArg }) {
        return (
          currentArg?.query.cursor === '' ||
          !isEqual(currentArg?.query, previousArg?.query)
        )
        //  return currentArg !== previousArg
      },
    }),
    updateAutoAuth: build.mutation<
      WiFiConfigType,
      {
        serial: string
        data: {
          type: string
        }
      }
    >({
      query: ({ serial, data }) => ({
        url: `/${serial}/config/auto-auto`,
        method: 'POST',
        data,
      }),
      invalidatesTags: (res: any) => [{ type: 'WiFiConfig', id: res?.serial }],
      transformResponse: (data: WiFiConfigType) => {
        toast.success('Settings updated')
        return data
      },
    }),
    removeAutoAuth: build.mutation<
      WiFiConfigType,
      {
        serial: string
        data: {
          type: string
        }
      }
    >({
      query: ({ serial, data }) => ({
        url: `/${serial}/config/auto-auto/${data.type}`,
        method: 'DELETE',
        data,
      }),
      invalidatesTags: (res: any) => [{ type: 'WiFiConfig', id: res?.serial }],
      transformResponse: (data: WiFiConfigType) => {
        toast.success('Settings updated')
        return data
      },
    }),
    updateSettings: build.mutation<
      WiFiConfigType,
      { serial: string; data: Partial<WiFiConfigType> }
    >({
      query: ({ serial, data }) => ({
        url: `/${serial}/config`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: (res: any) => [{ type: 'WiFiConfig', id: res?.serial }],
      transformResponse: (data: WiFiConfigType) => {
        toast.success('Settings updated')
        return data
      },
    }),
    getVendors: build.query<WifiVendorType[], unknown>({
      query: () => ({
        method: 'get',
        url: '/vendors',
      }),
      transformResponse: (items: WifiVendorType[] = []) => {
        return items.sort((a, b) => {
          if (a.name < b.name) {
            return -1
          }
          if (a.name > b.name) {
            return 1
          }
          return 0
        })
      },
    }),
    getFullInform: build.query<
      {
        inform: WiFiInformType & {
          config: {
            snap_config: {
              api_port_open: number
              ssl_port_open: number
              reprovision_on_next_heartbeat: boolean
              last_reprovision_at: string
            }
          }
        }
        logs: EventLogType[]
      },
      { serial: string; orgId: string }
    >({
      query: ({ serial, orgId }) => ({
        method: 'get',
        url: `/organisations/${orgId}/health/status`,
        params: {
          serial,
        },
      }),
      providesTags: (data: any) => [
        { type: 'WiFiInform', id: data?.inform?.id },
      ],
    }),
    getInform: build.query<WiFiInformType, { serial: string }>({
      query: ({ serial }) => ({
        method: 'get',
        url: `/${serial}/config/vendor`,
      }),
      providesTags: (data: any) => [{ type: 'WiFiInform', id: data?.id }],
    }),
    updateVendor: build.mutation<
      WiFiInformType,
      {
        serial: string
        vendor_id: string
        radius_secret?: string
        oauth_client_hostname?: string
        oauth_client_id?: string
        oauth_client_secret?: string
      }
    >({
      query: (data: any) => ({
        method: 'PUT',
        url: `/${data.serial}/config/vendor/${data.vendor_id}`,
        data,
      }),
      invalidatesTags: (res, _1, { serial }) => [
        { type: 'WiFiInform', id: res?.id },
        { type: 'MikrotikStatus', id: serial },
        { type: 'WiFiConfig', id: serial },
        { type: 'WiFiConfig', id: 'LIST' },
      ],
    }),
    getUniFiControllers: build.query<UniFiControllerType[], { serial: string }>(
      {
        query: ({ serial }) => ({
          method: 'get',
          url: `/${serial}/config/unifi`,
        }),
        providesTags: [{ type: 'UniFiControllers', id: 'LIST' }],
      }
    ),
    getUniFiControllerSite: build.query<UniFiConfigType, { serial: string }>({
      query: ({ serial }) => ({
        method: 'get',
        url: `/${serial}/config/unifi/site`,
      }),
      providesTags: (item: any) => [
        { type: 'UniFiControllerConfig', id: item?.id },
        { type: 'UniFiControllerConfig', id: 'LIST' },
      ],
    }),

    getUniFiControllerSites: build.query<
      { id?: string; name?: string; desc?: string }[],
      { serial: string; controllerId: string }
    >({
      query: ({ serial, controllerId }) => ({
        method: 'get',
        url: `/${serial}/config/unifi/${controllerId}`,
      }),
      providesTags: [{ type: 'UniFiLocations', id: 'LIST' }],
      transformResponse: (
        data: { id?: string; name?: string; desc?: string }[]
      ) => {
        return data.sort((a, b) => {
          const aName = a.desc ?? a.name ?? a.id ?? ''
          const bName = b.desc ?? b.name ?? b.id ?? ''
          return aName.localeCompare(bName)
        })
      },
    }),
    getUniFiControllerSitesSsids: build.query<
      { name: string; is_guest?: boolean }[],
      { serial: string; controllerId: string; siteId: string }
    >({
      query: ({ serial, controllerId, siteId }) => ({
        method: 'get',
        url: `/${serial}/config/unifi/${controllerId}/${siteId}`,
      }),
      providesTags: (_0, _1, { controllerId, siteId }) => [
        { type: 'UniFiLocations', id: `SSIDS-${controllerId}-${siteId}` },
      ],
    }),
    setUniFiSsidGuestPolicy: build.mutation<
      { name: string; is_guest: boolean },
      {
        serial: string
        controllerId: string
        siteId: string
        data: { name: string; is_guest: boolean }
      }
    >({
      query: ({ serial, controllerId, siteId, data }) => ({
        method: 'PUT',
        url: `/${serial}/config/unifi/${controllerId}/${siteId}/ssid`,
        data,
      }),
      invalidatesTags: (_0, _1, { controllerId, siteId }) => [
        { type: 'UniFiLocations', id: `SSIDS-${controllerId}-${siteId}` },
        { type: 'UniFiControllerConfig', id: 'LIST' },
      ],
    }),
    updateUniFiControllerSite: build.mutation<
      UniFiConfigType,
      {
        serial: string
        data: {
          controller_id: string
          site_id: string
          timeout: number
          multi_site_ssid: string
        }
      }
    >({
      query: ({ serial, data }) => ({
        method: 'POST',
        url: `/${serial}/config/unifi/site`,
        data,
      }),

      invalidatesTags: (item, _1, { data }) => [
        { type: 'UniFiControllerConfig', id: data.site_id },
        { type: 'WiFiConfig', id: 'LIST' },
        { type: 'UniFiControllers', id: 'LIST' },
        { type: 'UniFiControllerConfig', id: 'LIST' },
      ],
      transformResponse: (data: UniFiConfigType) => {
        toast.success('UniFi details updated')
        return data
      },
    }),
    createUniFiController: build.mutation<
      UniFiControllerType,
      {
        serial: string
        data: { hostname: string; username: string; password: string }
      }
    >({
      query: ({ serial, data }) => ({
        method: 'POST',
        url: `/${serial}/config/unifi`,
        data,
      }),
      invalidatesTags: [{ type: 'UniFiControllers', id: 'LIST' }],
      transformResponse: (data: UniFiControllerType) => {
        toast.success('UniFi controller created')
        return data
      },
    }),

    /**
     *
     */
    getPaymentPlans: build.query<
      PaymentPlanType[],
      {
        serial: string
      }
    >({
      query: ({ serial }) => ({
        method: 'get',
        url: `/${serial}/config/plans`,
      }),
      providesTags: (res: any) => [
        { type: 'PaymentPlans', id: 'LIST' },
        ...(res ?? []).map((item: any) => ({
          type: 'PaymentPlans' as const,
          id: item?.id,
        })),
      ],
      transformResponse: (data: PaymentPlanType[]) => {
        return [...data].sort((a, b) => {
          return a.amount - b.amount
        })
      },
    }),
    createPaymentPlan: build.mutation<
      string[],
      {
        serial: string
        data: Partial<PaymentPlanType>
      }
    >({
      query: ({ serial, data }) => ({
        method: 'POST',
        url: `/${serial}/config/plans`,
        data,
      }),
      invalidatesTags: [{ type: 'PaymentPlans', id: 'LIST' }],
      transformResponse: (data: string[]) => {
        toast.success('Payment plan created')
        return data
      },
    }),
    updatePaymentPlan: build.mutation<
      PaymentPlanType,
      {
        serial: string
        data: PaymentPlanType
      }
    >({
      query: ({ serial, data }) => ({
        method: 'POST',
        url: `/${serial}/config/plans/${data.id}`,
        data,
      }),
      invalidatesTags: (res: any) => [{ type: 'PaymentPlans', id: res?.id }],
      transformResponse: (data: PaymentPlanType) => {
        toast.success('Payment plan updated')
        return data
      },
    }),

    /**
     *
     *
     */
    removePaymentPlans: build.mutation<
      unknown,
      {
        serial: string
        plan_id: string
      }
    >({
      query: ({ serial, plan_id }) => ({
        method: 'DELETE',
        url: `/${serial}/config/plans/${plan_id}`,
      }),
      invalidatesTags: () => [
        {
          id: 'LIST',
          type: 'PaymentPlans',
        },
      ],
      transformResponse: (data: PaymentPlanType) => {
        toast.success('Payment plan deleted')
        return data
      },
    }),

    /**
     *
     */

    getGuestPayments: build.query<
      ServicePaginatedResponseType<GuestPaymentType>,
      {
        serial: string
        data: unknown
      }
    >({
      query: ({ serial, data }) => ({
        method: 'get',
        url: buildUrl(`/${serial}/guests/payments`, data),
        data,
      }),
      providesTags: [{ type: 'GuestPayments', id: 'LIST' }],
    }),
    createGuestPayments: build.mutation<
      unknown,
      {
        serial: string
        data: PaymentUpsertType
      }
    >({
      query: ({ serial, data }) => ({
        method: 'POST',
        url: `/${serial}/guests/payments`,
        data,
      }),
      invalidatesTags: [{ type: 'GuestPayments', id: 'LIST' }],
      transformResponse: (data: unknown) => {
        toast.success('Guest payment created')
        return data
      },
    }),
    updateGuestPayments: build.mutation<
      unknown,
      {
        serial: string
        data: PaymentUpsertType
      }
    >({
      query: ({ serial, data }) => ({
        method: 'PUT',
        url: `/${serial}/guests/payments/${data.id}`,
        data,
      }),
      invalidatesTags: [{ type: 'GuestPayments', id: 'LIST' }],
      transformResponse: (data: unknown) => {
        toast.success('Guest payment updated')
        return data
      },
    }),
    removeGuestPaymentDevice: build.mutation<
      unknown,
      {
        serial: string
        payment_id: string
        device_id: string
      }
    >({
      query: ({ serial, payment_id, device_id }) => ({
        method: 'DELETE',
        url: `/${serial}/guests/payments/${payment_id}/${device_id}`,
      }),
      invalidatesTags: [{ type: 'GuestPayments', id: 'LIST' }],
      transformResponse: (data: unknown) => {
        toast.success('Guest payment device deleted')
        return data
      },
    }),
    getWalledGarden: build.query<
      string[],
      {
        serial: string
      }
    >({
      query: ({ serial }) => ({
        method: 'get',
        url: `/${serial}/config/hardware/walled-garden`,
      }),
      providesTags: (_1, _2, { serial: id }) => [{ type: 'WiFiConfig', id }],
    }),
    getUserSessions: build.query<
      ServicePaginatedResponseType<WiFiSessionType>,
      {
        orgId: string
        cursor: string
        limit: number
        customerId: string
      }
    >({
      query: ({ orgId, customerId, cursor, limit }) => ({
        method: 'get',
        url: buildUrl(`/organisations/${orgId}/guest/${customerId}/sessions`, {
          cursor,
          limit,
        }),
      }),
    }),
    getUserImpressions: build.query<
      ServicePaginatedResponseType<WiFiImpressionsType>,
      {
        orgId: string
        cursor: string
        limit: number
        customerId: string
      }
    >({
      query: ({ orgId, customerId, cursor, limit }) => ({
        method: 'get',
        url: buildUrl(
          `/organisations/${orgId}/guest/${customerId}/impressions`,
          {
            cursor,
            limit,
          }
        ),
      }),
    }),
    getUserPayments: build.query<
      ServicePaginatedResponseType<GuestPaymentType>,
      {
        orgId: string
        cursor: string
        limit: number
        customerId: string
      }
    >({
      query: ({ orgId, customerId, cursor, limit }) => ({
        method: 'get',
        url: buildUrl(`/organisations/${orgId}/guest/${customerId}/payments`, {
          cursor,
          limit,
        }),
      }),
    }),

    /**
     * IMPRESSIONS EXPERIENCE REPORT
     */
    getExperienceReport: build.query<ExperienceReportType, { serial: string }>({
      query: ({ serial }) => ({
        method: 'get',
        url: `/${serial}/guests/impressions/experience-report`,
      }),
    }),

    /**
     * IMPRESSIONS EXPERIENCE SNAPSHOT
     */
    getExperienceSnapshot: build.query<
      ExperienceSnapshotType,
      { serial: string }
    >({
      query: ({ serial }) => ({
        method: 'get',
        url: `/${serial}/guests/impressions/experience-snapshot`,
      }),
    }),

    /**
     * MIKROTIK API START
     */
    getMikrotikLines: build.query<
      MikrotikLine[],
      {
        serial: string
      }
    >({
      query: ({ serial }) => ({
        method: 'get',
        url: `/${serial}/config/hardware/mikrotik/run-line`,
      }),
      providesTags: (_0, _1, { serial }) => [
        { type: 'MikrotikLines', id: serial },
      ],
    }),
    //
    deleteMikrotikLine: build.mutation<
      unknown,
      {
        serial: string
        line_id: string
      }
    >({
      query: ({ serial, line_id }) => ({
        method: 'DELETE',
        url: `/${serial}/config/hardware/mikrotik/run-line/${line_id}`,
      }),
      invalidatesTags: (_0, _1, { serial }) => [
        { type: 'MikrotikLines', id: serial },
      ],
    }),
    createMikrotikLine: build.mutation<
      {
        success: boolean
        message?: string
        edgeCacheApplied?: boolean
        assetHost?: string
        deviceLanIp?: string | null
        certificateName?: string | null
        [k: string]: unknown
      },
      {
        serial: string
        data: {
          payload: Record<string, any>
          action:
            | 'provision'
            | 'update'
            | 'system'
            | 'ssl'
            | 'walled_garden'
            | 'radius'
            | 'files'
            | 'wlan'
            | 'network'
            | 'services'
            | 'firewall'
            | 'reboot'
            | 'upgrade'
            | 'hosts'
            | 'health-check'
            | 'heart-beat'
            | 'proxy'
            | 'store-network-rule'
            | 'delete-network-rule'
            | 'store-firewall-rule'
            | 'delete-firewall-rule'
            | 'sync-firewall-rules'
            | 'store-hotspot-rule'
            | 'delete-hotspot-rule'
            | 'sync-hotspot-rules'
            | 'reset'
            | 'snap-guard'
            | 'sync-access-points'
            | 'enable-wireguard'
            | 'install-nearly-edge'
            | 'uninstall-nearly-edge'
        }
      }
    >({
      query: ({ serial, data }) => {
        const payload =
          data.action === 'install-nearly-edge'
            ? withRouterOsRuntimeOptIn(data.payload)
            : data.payload

        return {
          method: 'POST',
          url: `/${serial}/config/hardware/mikrotik/run-line`,
          data: {
            ...data,
            payload,
          },
        }
      },
      invalidatesTags: (_0, _1, { serial }) => [
        { type: 'MikrotikLines', id: serial },
      ],
    }),

    installEdgeRuntime: build.mutation<
      {
        success: boolean
        message?: string
        edgeCacheApplied?: boolean
        assetHost?: string
        deviceLanIp?: string | null
        certificateName?: string | null
        [k: string]: unknown
      },
      {
        serial: string
        deviceSerial?: string
        data?: {
          imageTag?: string
          registryHost?: string
          dockerRegistryUrl?: string
          includeImageProxy?: boolean
          imageProxyImageTag?: string
          envOverrides?: Record<string, string>
          imageProxyEnvOverrides?: Record<string, string>
          includePrinterAgent?: boolean
          printerAgentImageTag?: string
          /** Storage target chosen in the picker (a mount point, or `internal-flash`). */
          storageMountPoint?: string
        }
      }
    >({
      async queryFn({ serial, deviceSerial, data }, _api, _extra, baseQuery) {
        if (deviceSerial) {
          const unlockResult = await baseQuery({
            method: 'PATCH',
            url: `/api/snap-provisioning/${deviceSerial}`,
            data: {
              edge_runtime_status: 'installing',
              ...(data?.imageTag
                ? { edge_runtime_image_tag: data.imageTag }
                : {}),
            },
          })

          if (unlockResult.error) return { error: unlockResult.error }
        }

        const queueResult = await baseQuery({
          method: 'POST',
          url: `/${serial}/config/hardware/mikrotik/run-line`,
          data: {
            action: 'install-nearly-edge',
            payload: withRouterOsRuntimeOptIn({
              includeImageProxy: true,
              includePrinterAgent: true,
              ...(data ?? {}),
            }),
          },
        })

        if (queueResult.error) return { error: queueResult.error }

        return {
          data: queueResult.data as {
            success: boolean
            message?: string
            edgeCacheApplied?: boolean
            assetHost?: string
            deviceLanIp?: string | null
            certificateName?: string | null
            [k: string]: unknown
          },
        }
      },
      invalidatesTags: (_0, _1, { serial, deviceSerial }) => [
        { type: 'MikrotikLines', id: serial },
        { type: 'MikrotikStatus', id: `edge-runtime:${serial}` },
        ...(deviceSerial
          ? [{ type: 'SnapDevice' as const, id: deviceSerial }]
          : []),
      ],
    }),

    uninstallEdgeRuntime: build.mutation<
      {
        success: boolean
        message?: string
        error?: string
        details?: Record<string, unknown>
        [k: string]: unknown
      },
      { serial: string }
    >({
      query: ({ serial }) => ({
        method: 'POST',
        url: `/${serial}/config/hardware/mikrotik/run-line`,
        data: {
          action: 'uninstall-nearly-edge',
          payload: {},
        },
      }),
      invalidatesTags: (_0, _1, { serial }) => [
        { type: 'MikrotikLines', id: serial },
        { type: 'MikrotikStatus', id: `edge-runtime:${serial}` },
      ],
    }),

    // ========================================================================
    // Edge-runtime status — written by trinity's install-nearly-edge worker
    // when a capable MikroTik runs the whole nearly + redis stack as
    // RouterOS containers locally. This is the only on-device captive-portal
    // path; the legacy portal-sync / portal-cdn static-bundle path has been
    // retired.
    //
    // status: 'installing' | 'running' | 'failed' | 'uninstalled' | null
    // ========================================================================
    getEdgeRuntimeStatus: build.query<
      {
        status: 'installing' | 'running' | 'failed' | 'uninstalled' | null
        image_tag: string | null
        synced_at: string | null
        running_images?: {
          name?: string | null
          image?: string | null
          status?: string | null
          interface_url?: string | null
        }[]
        runningImages?: {
          name?: string | null
          image?: string | null
          status?: string | null
          interface_url?: string | null
        }[]
        live?: {
          containers?: Record<string, unknown>[]
          running_images?: Record<string, unknown>[]
          runningImages?: Record<string, unknown>[]
        } | null
      },
      { serial: string; live?: boolean }
    >({
      query: ({ serial, live }) => ({
        method: 'get',
        url: `/${serial}/edge-runtime`,
        params: live ? { live: true } : undefined,
      }),
      providesTags: (_0, _1, { serial }) => [
        { type: 'MikrotikStatus', id: `edge-runtime:${serial}` },
      ],
    }),

    // Storage targets the device can host containers on. The runtime never
    // picks a disk itself — the operator chooses one of these and it is sent
    // through on install.
    getEdgeRuntimeStorage: build.query<
      {
        success: boolean
        error?: string
        candidates: {
          id: string
          label: string
          mountPoint: string
          filesystem: string
          model: string | null
          interface: string | null
          sizeBytes: number | null
          freeBytes: number | null
          eligible: boolean
          reason: string
        }[]
      },
      { serial: string }
    >({
      query: ({ serial }) => ({
        method: 'get',
        url: `/${serial}/edge-runtime/storage`,
      }),
      providesTags: (_0, _1, { serial }) => [
        { type: 'MikrotikStatus', id: `edge-runtime:${serial}` },
      ],
    }),

    getContainerPresets: build.query<
      {
        success: boolean
        data: {
          id:
            | 'pihole'
            | 'stampede-wifi-proxy'
            | 'image-proxy'
            | 'redis'
            | 'snap-voice'
          label: string
          description: string
          image: string | null
          env: Record<string, string>
          services: {
            name: string
            protocol: 'http' | 'https' | 'tcp' | 'udp'
            port: number
            listenPort?: number
            path?: string
            primary?: boolean
            healthPath?: string
          }[]
          notes?: string[]
        }[]
      },
      { serial: string }
    >({
      query: ({ serial }) => ({
        method: 'get',
        url: `/${serial}/config/hardware/mikrotik/container/presets`,
      }),
      providesTags: (_0, _1, { serial }) => [
        { type: 'MikrotikStatus', id: `edge-runtime:${serial}` },
      ],
    }),

    getRunningContainers: build.query<
      {
        success: boolean
        data: {
          id?: string
          name?: string
          image?: string
          status?: string
          running?: boolean
          interface_url?: string | null
          interface?: string | null
          containerIp?: string | null
          services?: {
            name: string
            protocol: string
            port?: number
            listenPort?: number
            url?: string | null
          }[]
        }[]
        running_images: {
          name?: string | null
          image?: string | null
          status?: string | null
          interface_url?: string | null
        }[]
      },
      { serial: string }
    >({
      query: ({ serial }) => ({
        method: 'get',
        url: `/${serial}/config/hardware/mikrotik/container/running`,
      }),
      providesTags: (_0, _1, { serial }) => [
        { type: 'MikrotikStatus', id: `edge-runtime:${serial}` },
      ],
    }),

    provisionContainerPreset: build.mutation<
      {
        success: boolean
        data: {
          containerId: string
          image: string
          access: 'tunnel' | 'lan' | 'both'
          interface_url: string | null
          services: {
            name: string
            protocol: string
            target_port: number
            listen_port: number
            direct_url?: string | null
            tunnel_url?: string | null
            domain_url?: string | null
            wireguard_ip?: string | null
            domain_host?: string | null
          }[]
          health?: {
            ok: boolean | null
            skipped?: boolean
            reason?: string
            url?: string
            error?: string
            checked_at?: string
          }
          steps?: {
            name: string
            ok?: boolean
            skipped?: boolean
            optional?: boolean
            error?: string
          }[]
          message?: string
        }
      },
      {
        serial: string
        data: {
          preset:
            | 'pihole'
            | 'stampede-wifi-proxy'
            | 'image-proxy'
            | 'redis'
            | 'snap-voice'
          access?: 'tunnel' | 'lan' | 'both'
          start?: boolean
          imageTag?: string
          remoteImage?: string
          ghcrOwner?: string
          ghcrToken?: string
          env?: Record<string, string | number | boolean>
          ports?: string
          healthPath?: string
          registryUsername?: string
          registryPassword?: string
          enableSnapguardRuntime?: boolean
        }
      }
    >({
      query: ({ serial, data }) => ({
        method: 'POST',
        url: `/${serial}/config/hardware/mikrotik/container/provision/preset`,
        data: withRouterOsRuntimeOptIn(data),
      }),
      invalidatesTags: (_0, _1, { serial }) => [
        { type: 'MikrotikStatus', id: `edge-runtime:${serial}` },
      ],
    }),

    provisionHotspotRuntime: build.mutation<
      {
        success: boolean
        data: {
          containerId: string
          image: string
          access: 'tunnel' | 'lan' | 'both'
          interface_url: string | null
          services: {
            name: string
            protocol: string
            target_port: number
            listen_port: number
            direct_url?: string | null
            tunnel_url?: string | null
            domain_url?: string | null
            wireguard_ip?: string | null
            domain_host?: string | null
          }[]
          health?: {
            ok: boolean | null
            skipped?: boolean
            reason?: string
            url?: string
            error?: string
            checked_at?: string
          }
          dependencies?: {
            containerId: string
            image: string
            access: 'tunnel' | 'lan' | 'both'
            interface_url: string | null
            services: {
              name: string
              protocol: string
              target_port: number
              listen_port: number
              direct_url?: string | null
              tunnel_url?: string | null
              domain_url?: string | null
            }[]
          }[]
          optional?: {
            preset: string
            success: boolean
            error?: string
          }[]
          hotspot?: {
            domain?: string | null
            url?: string | null
            redis_uri?: string | null
            image_proxy?: string | null
          }
          steps?: {
            name: string
            ok?: boolean
            skipped?: boolean
            optional?: boolean
            error?: string
          }[]
          message?: string
        }
      },
      {
        serial: string
        data?: {
          access?: 'tunnel' | 'lan' | 'both'
          start?: boolean
          imageTag?: string
          remoteImage?: string
          ghcrOwner?: string
          ghcrToken?: string
          env?: Record<string, string | number | boolean>
          ports?: string
          healthPath?: string
          registryUsername?: string
          registryPassword?: string
          includeImageProxy?: boolean
          enableSnapguardRuntime?: boolean
        }
      }
    >({
      query: ({ serial, data }) => ({
        method: 'POST',
        url: `/${serial}/config/hardware/mikrotik/container/provision/hotspot-runtime`,
        data: withRouterOsRuntimeOptIn(data ?? {}),
      }),
      invalidatesTags: (_0, _1, { serial }) => [
        { type: 'MikrotikStatus', id: `edge-runtime:${serial}` },
      ],
    }),

    runContainerImage: build.mutation<
      {
        success: boolean
        data: {
          containerId: string
          image: string
          access: 'tunnel' | 'lan' | 'both'
          interface_url: string | null
          services: {
            name: string
            protocol: string
            target_port: number
            listen_port: number
            direct_url?: string | null
            tunnel_url?: string | null
            domain_url?: string | null
          }[]
          health?: {
            ok: boolean | null
            skipped?: boolean
            reason?: string
            url?: string
            error?: string
            checked_at?: string
          }
          steps?: {
            name: string
            ok?: boolean
            skipped?: boolean
            optional?: boolean
            error?: string
          }[]
          message?: string
        }
      },
      {
        serial: string
        data: {
          image: string
          name?: string
          access?: 'tunnel' | 'lan' | 'both'
          start?: boolean
          env?: Record<string, string | number | boolean>
          ports?: string
          healthPath?: string
          registryUrl?: string
          registryUsername?: string
          registryPassword?: string
          enableSnapguardRuntime?: boolean
        }
      }
    >({
      query: ({ serial, data }) => ({
        method: 'POST',
        url: `/${serial}/config/hardware/mikrotik/container/run`,
        data: withRouterOsRuntimeOptIn(data),
      }),
      invalidatesTags: (_0, _1, { serial }) => [
        { type: 'MikrotikStatus', id: `edge-runtime:${serial}` },
      ],
    }),

    // Enable WireGuard directly (no queue) - uses direct API connection
    enableWireguardDirect: build.mutation<
      {
        success: boolean
        message?: string
        needs_provisioning?: boolean
        data?: {
          interface_created: boolean
          was_disabled: boolean
          config_applied: boolean
          device_ip: string
          server_endpoint: string
          server_port: number
        }
      },
      {
        serial: string
      }
    >({
      query: ({ serial }) => ({
        method: 'POST',
        url: `/${serial}/config/hardware/mikrotik/enable-wireguard-direct`,
        data: {},
      }),
      invalidatesTags: (_0, _1, { serial }) => [
        { type: 'MikrotikHealth', id: serial },
      ],
    }),

    //
    getInterfaces: build.query<
      InterfaceSchemaType[],
      {
        serial: string
      }
    >({
      query: ({ serial }) => ({
        method: 'get',
        url: `/${serial}/config/hardware/mikrotik/interfaces`,
      }),
      providesTags: (_0, _1, { serial }) => [
        { type: 'MikrotikInterfaces', id: serial },
      ],
    }),
    toggleInterface: build.mutation<
      { success: boolean },
      {
        serial: string
        interfaceId: string
        disabled: boolean
      }
    >({
      query: ({ serial, interfaceId, disabled }) => ({
        method: 'POST',
        url: `/${serial}/config/hardware/mikrotik/interfaces/${interfaceId}`,
        data: { disabled },
      }),
      invalidatesTags: (_0, _1, { serial }) => [
        { type: 'MikrotikInterfaces', id: serial },
      ],
    }),
    getSystem: build.query<
      SystemSchemaType,
      {
        serial: string
      }
    >({
      query: ({ serial }) => ({
        method: 'get',
        url: `/${serial}/config/hardware/mikrotik/system`,
      }),
      providesTags: (_0, _1, { serial }) => [
        { type: 'MikrotikSystem', id: serial },
      ],
    }),
    updateSystem: build.mutation<
      SystemSchemaType,
      {
        serial: string
        data: {
          apply_port_config: boolean
        }
      }
    >({
      query: ({ serial, data }) => ({
        method: 'POST',
        url: `/${serial}/config/hardware/mikrotik/system`,
        data,
      }),
      invalidatesTags: (_0, _1, { serial }) => [
        { type: 'MikrotikSystem', id: serial },
      ],
    }),
    getMikrotikStatus: build.query<
      {
        id: string
        config_id: string
        certificate_expires_at: string
        created_at: string
        last_applied_at: string
        last_updated_at: string
        device_mac: string
        board_name: string
        version: string
        hardware_version: string
        api_port_open: boolean
        ssl_port_open: boolean
        can_login_to_api: boolean
        reprovision_on_next_heartbeat: boolean
        resolved_ddns_address: string
        is_advanced_deployment: boolean
        last_connection_method?: string
        vpn_latency_ms: number | null
        vpn_last_seen: string | null
        vpn_is_online: boolean
        provisioning?: {
          id: string
          created_at: string
          updated_at: string
          status: string
          wan_ip: string
          lan_ip: string
          device_mac: string
          device_password: string | null
          device_serial: string
          device_board_name: string
          device_model: string
          device_firmware_version: string
          config_id: string
          vpn_connected: boolean
          ownership_organisation_id: string
        }
        vpn_server_hostname?: string | null
        vpn_device_info?: {
          serial_number: string
          name: string
          model: string | null
          ip_address: string
          enabled: boolean
          last_seen: string | null
          transfer_rx: string
          transfer_tx: string
          created_at: string
          updated_at: string
          is_online: boolean
          vpn_server_id: string
          vpn_server_hostname: string
        }
        provisioning_status?: string
        provisioning_started_at?: string | null
        provisioning_attempts?: number
        provisioning_error?: string | null
        auto_update?: boolean
        auto_update_time?: string | null
        auto_update_channel?: string
        can_login_using_api?: boolean
      },
      {
        serial: string
      }
    >({
      query: ({ serial }) => ({
        method: 'get',
        url: `/${serial}/config/hardware/mikrotik/status`,
        headers: {
          'Cache-Control': 'no-cache',
        },
      }),
      providesTags: (_0, _1, { serial }) => [
        { type: 'MikrotikStatus', id: serial },
      ],
    }),
    updateMikrotikConfig: build.mutation<
      unknown,
      {
        serial: string
        data: {
          is_advanced_deployment: boolean
        }
      }
    >({
      query: ({ serial, data }) => ({
        method: 'PUT',
        url: `/${serial}/config/hardware/snap-config`,
        data,
      }),
      invalidatesTags: (_0, _1, { serial }) => [
        { type: 'MikrotikStatus', id: serial },
      ],
    }),

    getMikrotikRadius: build.query<
      RadiusSchemaType[],
      {
        serial: string
      }
    >({
      query: ({ serial }) => ({
        method: 'get',
        url: `/${serial}/config/hardware/mikrotik/radius`,
      }),
      providesTags: (_0, _1, { serial }) => [
        { type: 'MikrotikRadius', id: serial },
      ],
    }),
    setMikrotikRadius: build.mutation<
      RadiusSchemaType[],
      {
        serial: string
      }
    >({
      query: ({ serial }) => ({
        method: 'POST',
        url: `/${serial}/config/hardware/mikrotik/radius`,
      }),
      invalidatesTags: (_0, _1, { serial }) => [
        { type: 'MikrotikRadius', id: serial },
      ],
    }),
    getMikrotikHotspot: build.query<
      {
        hotspot: HotspotSchemaType
        profiles: HotspotProfileSchemaType
      },
      {
        serial: string
      }
    >({
      query: ({ serial }) => ({
        method: 'get',
        url: `/${serial}/config/hardware/mikrotik/hotspot`,
      }),
      providesTags: (_0, _1, { serial }) => [
        { type: 'MikrotikHotspot', id: serial },
      ],
    }),
    setMikrotikHotspot: build.mutation<
      {
        hotspot: HotspotSchemaType
        profiles: HotspotProfileSchemaType
      },
      {
        serial: string
        data: {
          interface: string
        }
      }
    >({
      query: ({ serial, data }) => ({
        method: 'POST',
        url: `/${serial}/config/hardware/mikrotik/hotspot`,
        data,
      }),
      invalidatesTags: (_0, _1, { serial }) => [
        { type: 'MikrotikHotspot', id: serial },
      ],
    }),
    getMikrotikNetwork: build.query<
      {
        addresses: AddressSchemaType
        dhcp_server: DHCPServerSchemaType
        dhcp_server_network: DHCPServerNetworkSchemaType
        dns: DNSSchemaType
        firewall: FirewallSchemaType[]
        pool: PoolSchemaType
      },
      {
        serial: string
      }
    >({
      query: ({ serial }) => ({
        method: 'get',
        url: `/${serial}/config/hardware/mikrotik/network`,
      }),
      providesTags: (_0, _1, { serial }) => [
        { type: 'MikrotikNetwork', id: serial },
      ],
    }),
    updateMikrotikNetwork: build.mutation<
      {
        addresses: AddressSchemaType
        dhcp_server: DHCPServerSchemaType
        dhcp_server_network: DHCPServerNetworkSchemaType
        dns: DNSSchemaType
        firewall: FirewallSchemaType[]
        pool: PoolSchemaType
      },
      {
        serial: string
        data: {
          interface: string
        }
      }
    >({
      query: ({ serial, data }) => ({
        method: 'POST',
        url: `/${serial}/config/hardware/mikrotik/network`,
        data,
      }),
      invalidatesTags: (_0, _1, { serial }) => [
        { type: 'MikrotikNetwork', id: serial },
      ],
    }),
    getMikrotikSsl: build.query<
      SSLSchemaType,
      {
        serial: string
      }
    >({
      query: ({ serial }) => ({
        method: 'get',
        url: `/${serial}/config/hardware/mikrotik/ssl`,
      }),
      providesTags: (_0, _1, { serial }) => [
        { type: 'MikrotikSsl', id: serial },
      ],
    }),
    createMikrotikSsl: build.mutation<
      { progress: string; '.section': string }[],
      {
        serial: string
      }
    >({
      query: ({ serial }) => ({
        method: 'POST',
        url: `/${serial}/config/hardware/mikrotik/ssl`,
      }),
      invalidatesTags: (_0, _1, { serial }) => [
        { type: 'MikrotikSsl', id: serial },
      ],
    }),
    getHotspot: build.query<
      {
        hotspot: HotspotSchemaType
        profiles: HotspotProfileSchemaType
      },
      {
        serial: string
      }
    >({
      query: ({ serial }) => ({
        method: 'get',
        url: `/${serial}/config/hardware/mikrotik/hotspot`,
      }),
      providesTags: (_0, _1, { serial }) => [
        { type: 'MikrotikHotspot', id: serial },
      ],
    }),
    rebootMikrotik: build.mutation<
      unknown,
      {
        serial: string
      }
    >({
      extraOptions: { maxRetries: 0 },
      query: ({ serial }) => ({
        method: 'POST',
        url: `/${serial}/config/hardware/mikrotik/reboot`,
      }),
    }),

    resetSnapDevice: build.mutation<
      {
        success: boolean
        message: string
        provisioning_token?: string
        organisation_id?: string
        enable_vpn?: boolean
      },
      {
        serial: string
      }
    >({
      extraOptions: { maxRetries: 0 },
      query: ({ serial }) => ({
        method: 'POST',
        url: `/${serial}/config/hardware/mikrotik/reset`,
        data: {},
      }),
      invalidatesTags: (_0, _1, { serial }) => [
        { type: 'WiFiInform', id: serial },
        { type: 'MikrotikLines', id: serial },
        { type: 'MikrotikHealthCheck', id: serial },
      ],
    }),

    resetAndProvisionSnapDevice: build.mutation<
      {
        success: boolean
        message: string
      },
      {
        serial: string
      }
    >({
      extraOptions: { maxRetries: 0 },
      query: ({ serial }) => ({
        method: 'POST',
        url: `/${serial}/config/hardware/mikrotik/reset-and-provision`,
        data: {},
      }),
      invalidatesTags: (_0, _1, { serial }) => [
        { type: 'WiFiInform', id: serial },
        { type: 'MikrotikLines', id: serial },
        { type: 'MikrotikHealthCheck', id: serial },
      ],
    }),

    // Device Upgrade Endpoints
    upgradeCheckDevice: build.mutation<
      {
        success: boolean
        message: string
        channel: 'stable' | 'long-term' | 'testing'
      },
      {
        serial: string
        channel?: 'stable' | 'long-term' | 'testing'
      }
    >({
      extraOptions: { maxRetries: 0 },
      query: ({ serial, channel = 'stable' }) => ({
        method: 'POST',
        url: `/${serial}/config/hardware/mikrotik/upgrade/check`,
        data: { channel },
      }),
      invalidatesTags: (_0, _1, { serial }) => [
        { type: 'WiFiInform', id: serial },
        { type: 'MikrotikLines', id: serial },
      ],
    }),

    upgradeDownloadDevice: build.mutation<
      {
        success: boolean
        message: string
        channel: 'stable' | 'long-term' | 'testing'
      },
      {
        serial: string
        channel?: 'stable' | 'long-term' | 'testing'
      }
    >({
      extraOptions: { maxRetries: 0 },
      query: ({ serial, channel = 'stable' }) => ({
        method: 'POST',
        url: `/${serial}/config/hardware/mikrotik/upgrade/download`,
        data: { channel },
      }),
      invalidatesTags: (_0, _1, { serial }) => [
        { type: 'WiFiInform', id: serial },
        { type: 'MikrotikLines', id: serial },
      ],
    }),

    upgradeInstallDevice: build.mutation<
      {
        success: boolean
        message: string
        channel: 'stable' | 'long-term' | 'testing'
      },
      {
        serial: string
        channel?: 'stable' | 'long-term' | 'testing'
      }
    >({
      extraOptions: { maxRetries: 0 },
      query: ({ serial, channel = 'stable' }) => ({
        method: 'POST',
        url: `/${serial}/config/hardware/mikrotik/upgrade/install`,
        data: { channel },
      }),
      invalidatesTags: (_0, _1, { serial }) => [
        { type: 'WiFiInform', id: serial },
        { type: 'MikrotikLines', id: serial },
        { type: 'MikrotikHealthCheck', id: serial },
      ],
    }),

    upgradeRouterboardDevice: build.mutation<
      {
        success: boolean
        message: string
        current_firmware: string
        upgrade_firmware: string
        reboot_required: boolean
      },
      {
        serial: string
      }
    >({
      extraOptions: { maxRetries: 0 },
      query: ({ serial }) => ({
        method: 'POST',
        url: `/${serial}/config/hardware/mikrotik/upgrade/routerboard`,
        data: {},
      }),
      invalidatesTags: (_0, _1, { serial }) => [
        { type: 'WiFiInform', id: serial },
        { type: 'MikrotikLines', id: serial },
        { type: 'MikrotikHealthCheck', id: serial },
      ],
    }),

    // Auto-Update Configuration Endpoints
    getAutoUpdateConfig: build.query<
      {
        auto_update: boolean
        auto_update_time: string
        auto_update_channel: 'stable' | 'long-term' | 'testing'
        last_auto_update_at?: string
        last_auto_update_version?: string
      },
      {
        serial: string
      }
    >({
      query: ({ serial }) => ({
        method: 'GET',
        url: `/${serial}/config/hardware/mikrotik/auto-update/config`,
      }),
      providesTags: (_0, _1, { serial }) => [
        { type: 'WiFiInform', id: serial },
        { type: 'MikrotikStatus', id: serial },
      ],
    }),

    updateAutoUpdateConfig: build.mutation<
      {
        success: boolean
        message: string
        config: {
          auto_update: boolean
          auto_update_time?: string
          auto_update_channel?: 'stable' | 'long-term' | 'testing'
        }
      },
      {
        serial: string
        auto_update: boolean
        auto_update_time?: string
        auto_update_channel?: 'stable' | 'long-term' | 'testing'
      }
    >({
      query: ({ serial, ...data }) => ({
        method: 'POST',
        url: `/${serial}/config/hardware/mikrotik/auto-update/config`,
        data,
      }),
      invalidatesTags: (_0, _1, { serial }) => [
        { type: 'WiFiInform', id: serial },
        { type: 'MikrotikStatus', id: serial },
      ],
    }),

    // eSIM Management Endpoints
    provisionESim: build.mutation<
      {
        success: boolean
        message: string
        profile: {
          id: string
          telnyx_sim_id: string
          iccid: string
          activation_code: string
          mikrotik_provisioned: boolean
        }
      },
      {
        serial: string
        interfaceName?: string
        routeDistance?: number
        apn?: string
        username?: string
        password?: string
      }
    >({
      query: ({ serial, ...data }) => ({
        method: 'POST',
        url: `/${serial}/config/hardware/mikrotik/esim/provision`,
        data,
      }),
      invalidatesTags: (_0, _1, { serial }) => [
        { type: 'WiFiInform', id: serial },
        { type: 'MikrotikLines', id: serial },
      ],
    }),

    getESimStatus: build.query<
      {
        success: boolean
        message?: string
        profile: {
          id: string
          iccid: string
          status: string
          phone_number?: string
          interface_name: string
          apn: string
          route_distance: number
          provisioned: boolean
          provisioned_at?: string
          activated_at?: string
          suspended_at?: string
          usage?: {
            data_used_mb: number
            period_start: string
            period_end: string
          }
        } | null
      },
      { serial: string }
    >({
      query: ({ serial }) => ({
        method: 'GET',
        url: `/${serial}/config/hardware/mikrotik/esim/status`,
      }),
    }),

    activateESim: build.mutation<
      {
        success: boolean
        message: string
        error?: string
      },
      { serial: string }
    >({
      query: ({ serial }) => ({
        method: 'POST',
        url: `/${serial}/config/hardware/mikrotik/esim/activate`,
      }),
      invalidatesTags: (_0, _1, { serial }) => [
        { type: 'WiFiInform', id: serial },
        { type: 'MikrotikLines', id: serial },
      ],
    }),

    deactivateESim: build.mutation<
      {
        success: boolean
        message: string
      },
      { serial: string }
    >({
      query: ({ serial }) => ({
        method: 'DELETE',
        url: `/${serial}/config/hardware/mikrotik/esim/deactivate`,
      }),
      invalidatesTags: (_0, _1, { serial }) => [
        { type: 'WiFiInform', id: serial },
        { type: 'MikrotikLines', id: serial },
      ],
    }),

    getESimUsage: build.query<
      {
        success: boolean
        profile_id: string
        iccid: string
        total_usage_mb: number
        total_usage_gb: string
        usage_logs: {
          id: string
          data_used_mb: number
          period_start: string
          period_end: string
        }[]
      },
      { serial: string; days?: number }
    >({
      query: ({ serial, days = 30 }) => ({
        method: 'GET',
        url: `/${serial}/config/hardware/mikrotik/esim/usage?days=${days}`,
      }),
    }),

    reprovisionESim: build.mutation<
      {
        success: boolean
        message: string
        error?: string
        mikrotik_provisioned?: boolean
      },
      { serial: string }
    >({
      query: ({ serial }) => ({
        method: 'POST',
        url: `/${serial}/config/hardware/mikrotik/esim/reprovision`,
      }),
      invalidatesTags: (_0, _1, { serial }) => [
        { type: 'WiFiInform', id: serial },
        { type: 'MikrotikLines', id: serial },
      ],
    }),

    getMikrotikWalledGarded: build.query<
      {
        walled_garden: WalledGardenIpSchemaType[]
        ips: WalledGardenIpSchemaType[]
      },
      {
        serial: string
      }
    >({
      query: ({ serial }) => ({
        method: 'get',
        url: `/${serial}/config/hardware/mikrotik/walled-garden`,
      }),
      providesTags: (_0, _1, { serial }) => [
        { type: 'MikrotikWalledGarden', id: serial },
      ],
    }),
    updateMikrotikWalledGarded: build.mutation<
      unknown,
      {
        serial: string
      }
    >({
      query: ({ serial }) => ({
        method: 'POST',
        url: `/${serial}/config/hardware/mikrotik/walled-garden`,
      }),
      invalidatesTags: (_0, _1, { serial }) => [
        { type: 'MikrotikWalledGarden', id: serial },
      ],
    }),

    getMikrotikFiles: build.query<
      FileSchemaType[],
      {
        serial: string
      }
    >({
      query: ({ serial }) => ({
        method: 'get',
        url: `/${serial}/config/hardware/mikrotik/files`,
      }),
      providesTags: (_0, _1, { serial }) => [
        { type: 'MikrotikFiles', id: serial },
      ],
    }),
    updateMikrotikFiles: build.mutation<
      unknown,
      {
        serial: string
      }
    >({
      query: ({ serial }) => ({
        method: 'POST',
        url: `/${serial}/config/hardware/mikrotik/files`,
      }),
      invalidatesTags: (_0, _1, { serial }) => [
        { type: 'MikrotikFiles', id: serial },
      ],
    }),
    changeHeartbeat: build.mutation<
      {
        serial: 'QBRNYCIEGJSA'
      },
      {
        serial: string
        status:
          | 'OFFLINE'
          | 'ONLINE'
          | 'IP_UPDATE'
          | 'PAUSED'
          | 'MISSED'
          | 'REQUIRES_UPDATE'
      }
    >({
      query: ({ serial, status }) => ({
        method: 'PUT',
        url: `/${serial}/config/hardware/health`,
        data: {
          status,
        },
      }),
      invalidatesTags: (_0, _1, { serial }) => [
        { type: 'NetworkHeartbeat', id: serial },
      ],
    }),
    getHeartbeats: build.query<
      ServicePaginatedResponseType<{
        created_at: string
        id: string
        inform_id: string
        ip: string
        inform: WiFiInformType
        config: WiFiConfigType
        serial: 'QBRNYCIEGJSA'
        status:
          | 'OFFLINE'
          | 'ONLINE'
          | 'IP_UPDATE'
          | 'PAUSED'
          | 'MISSED'
          | 'REQUIRES_UPDATE'
      }>,
      {
        orgId: string
        serial?: string
        cursor?: string
        limit?: number
        types?: string[]
      }
    >({
      query: ({ orgId, serial, cursor, limit, types }) => ({
        method: 'get',
        url: `/organisations/${orgId}/health`,
        params: {
          serial,
          cursor,
          limit,
          types,
        },
      }),
      keepUnusedDataFor: 0,
      providesTags: (_0, _1, { orgId }) => [
        { type: 'NetworkHeartbeat', id: orgId },
      ],
      serializeQueryArgs: ({ endpointName }) => {
        return endpointName
      },
      merge: (currentCache, newItems, { arg: currentArg }) => {
        // If this is a new page for the same serial, merge the data
        if (currentArg.cursor) {
          currentCache.links.next = newItems.links.next

          newItems.data.forEach((item: any) => {
            const current = currentCache.data.findIndex(
              (it: any) => it?.id === item?.id
            )
            if (current >= 0) {
              currentCache.data[current] = item
            } else {
              currentCache.data.push(item)
            }
          })
        } else {
          // If no cursor (new serial), replace the entire cache
          currentCache.data = newItems.data
          currentCache.links = newItems.links
        }
      },
      forceRefetch({ currentArg, previousArg }) {
        return (
          currentArg?.cursor !== previousArg?.cursor ||
          currentArg?.serial !== previousArg?.serial ||
          currentArg?.types !== previousArg?.types
        )
      },
    }),
    getHeartbeat: build.query<
      {
        created_at: string
        id: string
        inform_id: string
        ip: string
        serial: 'QBRNYCIEGJSA'
        status:
          | 'OFFLINE'
          | 'ONLINE'
          | 'IP_UPDATE'
          | 'PAUSED'
          | 'MISSED'
          | 'REQUIRES_UPDATE'
      }[],
      {
        serial: string
      }
    >({
      query: ({ serial }) => ({
        method: 'get',
        url: `/${serial}/config/hardware/health`,
      }),
      keepUnusedDataFor: 0,
      providesTags: (_0, _1, { serial }) => [
        { type: 'NetworkHeartbeat', id: serial },
      ],
    }),
    getMikrotikHosts: build.query<
      {
        id: string
        DHCP: string
        address: string
        authorized: string
        bypassed: string
        'bytes-in': string
        'bytes-out': string
        'found-by': string
        'host-dead-time': string
        'idle-time': string
        'idle-timeout': string
        'mac-address': string
        'packets-in': string
        'packets-out': string
        server: string
        'to-address': string
        uptime: string
      }[],
      {
        serial: string
      }
    >({
      query: ({ serial }) => ({
        method: 'get',
        url: `/${serial}/config/hardware/mikrotik/hotspot/hosts`,
      }),
      providesTags: (_0, _1, { serial }) => [
        { type: 'MikrotikHosts', id: serial },
      ],
    }),
    removeMikrotikHosts: build.mutation<
      {
        id: string
        DHCP: string
        address: string
        authorized: string
        bypassed: string
        'bytes-in': string
        'bytes-out': string
        'found-by': string
        'host-dead-time': string
        'idle-time': string
        'idle-timeout': string
        'mac-address': string
        'packets-in': string
        'packets-out': string
        server: string
        'to-address': string
        uptime: string
      }[],
      {
        serial: string
        remove_macs: string[]
      }
    >({
      query: ({ serial, remove_macs }) => ({
        method: 'POST',
        url: `/${serial}/config/hardware/mikrotik/hotspot/hosts`,
        data: {
          remove_macs,
        },
      }),
      invalidatesTags: (_0, _1, { serial }) => [
        { type: 'MikrotikHosts', id: serial },
      ],
    }),
    getPolicyGroups: build.query<
      RadiusGroupPolicyType[],
      {
        serial: string
      }
    >({
      query: ({ serial }) => ({
        method: 'get',
        url: `/${serial}/config/hardware/group`,
      }),
      providesTags: (_0, _1, { serial }) => [
        { type: 'RadiusPolicyGroups', id: serial },
      ],
    }),
    updatePolicyGroup: build.mutation<
      RadiusGroupPolicyType,
      {
        serial: string
        data: RadiusGroupPolicyType
      }
    >({
      query: ({ serial, data }) => ({
        method: 'PUT',
        url: `/${serial}/config/hardware/group/${data.id}`,
        data,
      }),
      invalidatesTags: (_0, _1, { serial }) => [
        { type: 'RadiusPolicyGroups', id: serial },
      ],
    }),
    //config_id, created_at, payment_plan_id
    createPolicyGroup: build.mutation<
      RadiusGroupPolicyType,
      {
        serial: string
        data: Omit<RadiusGroupPolicyType, 'id' | 'config_id' | 'created_at'>
      }
    >({
      query: ({ serial, data }) => ({
        method: 'POST',
        url: `/${serial}/config/hardware/group`,
        data,
      }),
      invalidatesTags: (_0, _1, { serial }) => [
        { type: 'RadiusPolicyGroups', id: serial },
      ],
    }),
    deletePolicyGroup: build.mutation<
      RadiusGroupPolicyType,
      {
        serial: string
        data: RadiusGroupPolicyType
      }
    >({
      query: ({ serial, data }) => ({
        method: 'DELETE',
        url: `/${serial}/config/hardware/group/${data.id}`,
      }),
      invalidatesTags: (_0, _1, { serial }) => [
        { type: 'RadiusPolicyGroups', id: serial },
      ],
    }),
    getApplicationQosCatalog: build.query<
      ApplicationQosCatalogItemType[],
      { serial: string }
    >({
      query: ({ serial }) => ({
        method: 'get',
        url: `/${serial}/config/hardware/app-qos/catalog`,
      }),
      providesTags: (_0, _1, { serial }) => [
        { type: 'ApplicationQos', id: `${serial}:catalog` },
      ],
    }),
    getApplicationQosCustomApps: build.query<
      ApplicationQosCustomApplicationType[],
      { serial: string }
    >({
      query: ({ serial }) => ({
        method: 'get',
        url: `/${serial}/config/hardware/app-qos/custom-apps`,
      }),
      providesTags: (_0, _1, { serial }) => [
        { type: 'ApplicationQos', id: `${serial}:custom` },
      ],
    }),
    createApplicationQosCustomApp: build.mutation<
      ApplicationQosCustomApplicationType,
      { serial: string; data: ApplicationQosCustomApplicationPayload }
    >({
      query: ({ serial, data }) => ({
        method: 'POST',
        url: `/${serial}/config/hardware/app-qos/custom-apps`,
        data,
      }),
      invalidatesTags: (_0, _1, { serial }) => [
        { type: 'ApplicationQos', id: `${serial}:custom` },
        { type: 'ApplicationQos', id: `${serial}:policies` },
      ],
    }),
    updateApplicationQosCustomApp: build.mutation<
      ApplicationQosCustomApplicationType,
      {
        serial: string
        id: string
        data: ApplicationQosCustomApplicationPayload
      }
    >({
      query: ({ serial, id, data }) => ({
        method: 'PUT',
        url: `/${serial}/config/hardware/app-qos/custom-apps/${id}`,
        data,
      }),
      invalidatesTags: (_0, _1, { serial }) => [
        { type: 'ApplicationQos', id: `${serial}:custom` },
        { type: 'ApplicationQos', id: `${serial}:policies` },
      ],
    }),
    deleteApplicationQosCustomApp: build.mutation<
      ApplicationQosCustomApplicationType,
      { serial: string; id: string }
    >({
      query: ({ serial, id }) => ({
        method: 'DELETE',
        url: `/${serial}/config/hardware/app-qos/custom-apps/${id}`,
      }),
      invalidatesTags: (_0, _1, { serial }) => [
        { type: 'ApplicationQos', id: `${serial}:custom` },
        { type: 'ApplicationQos', id: `${serial}:policies` },
      ],
    }),
    getApplicationQosPolicies: build.query<
      ApplicationQosPolicyType[],
      { serial: string }
    >({
      query: ({ serial }) => ({
        method: 'get',
        url: `/${serial}/config/hardware/app-qos/policies`,
      }),
      providesTags: (_0, _1, { serial }) => [
        { type: 'ApplicationQos', id: `${serial}:policies` },
      ],
    }),
    createApplicationQosPolicy: build.mutation<
      ApplicationQosPolicyType,
      { serial: string; data: ApplicationQosPolicyPayload }
    >({
      query: ({ serial, data }) => ({
        method: 'POST',
        url: `/${serial}/config/hardware/app-qos/policies`,
        data,
      }),
      invalidatesTags: (_0, _1, { serial }) => [
        { type: 'ApplicationQos', id: `${serial}:policies` },
      ],
    }),
    updateApplicationQosPolicy: build.mutation<
      ApplicationQosPolicyType,
      { serial: string; id: string; data: ApplicationQosPolicyPayload }
    >({
      query: ({ serial, id, data }) => ({
        method: 'PUT',
        url: `/${serial}/config/hardware/app-qos/policies/${id}`,
        data,
      }),
      invalidatesTags: (_0, _1, { serial }) => [
        { type: 'ApplicationQos', id: `${serial}:policies` },
      ],
    }),
    deleteApplicationQosPolicy: build.mutation<
      ApplicationQosPolicyType,
      { serial: string; id: string }
    >({
      query: ({ serial, id }) => ({
        method: 'DELETE',
        url: `/${serial}/config/hardware/app-qos/policies/${id}`,
      }),
      invalidatesTags: (_0, _1, { serial }) => [
        { type: 'ApplicationQos', id: `${serial}:policies` },
      ],
    }),
    // ── WAN total-capacity settings + remaining-capacity report ──────────────
    getWanSettings: build.query<WanSettingsType, { serial: string }>({
      query: ({ serial }) => ({
        method: 'get',
        url: `/${serial}/config/hardware/mikrotik/wan`,
      }),
      providesTags: (_0, _1, { serial }) => [
        { type: 'WanSettings', id: serial },
      ],
    }),
    updateWanSettings: build.mutation<
      { success: boolean; config: WanSettingsType; rebuild_required?: boolean },
      {
        serial: string
        data: Partial<Omit<WanSettingsType, 'wan_uplink'>> & {
          /**
           * Uplink change (applied on the next build). Pass the password only
           * when setting/rotating it; omit to keep the stored one. `null`
           * clears back to auto-detect.
           */
          wan_uplink?: {
            mode: WanUplinkMode
            pppoe?: { username: string; password?: string; service?: string }
            static?: { address: string; gateway: string; dns?: string[] }
          } | null
        }
      }
    >({
      query: ({ serial, data }) => ({
        method: 'PUT',
        url: `/${serial}/config/hardware/mikrotik/wan`,
        data,
      }),
      // WAN ceiling changes the remaining capacity available to guarantees.
      invalidatesTags: (_0, _1, { serial }) => [
        { type: 'WanSettings', id: serial },
        { type: 'WanCapacity', id: serial },
      ],
    }),
    getWanCapacity: build.query<CapacityReportType, { serial: string }>({
      query: ({ serial }) => ({
        method: 'get',
        url: `/${serial}/config/hardware/mikrotik/capacity`,
      }),
      providesTags: (_0, _1, { serial }) => [
        { type: 'WanCapacity', id: serial },
      ],
    }),
    // KICK OFF an on-device WAN bandwidth test. The test runs download + upload
    // btest passes and can take 60s+ — far longer than the HTTP client will hold
    // a request — so this returns immediately with a run_id. Poll
    // getWanSpeedTestStatus with that run_id until it reports done/error.
    runWanSpeedTest: build.mutation<
      {
        success: boolean
        status?: 'running'
        run_id?: string
        message?: string
      },
      { serial: string; durationSec?: number }
    >({
      query: ({ serial, durationSec }) => ({
        method: 'POST',
        url: `/${serial}/config/hardware/mikrotik/speed-test`,
        data: durationSec ? { durationSec } : undefined,
      }),
    }),
    // Poll the status of a run started by runWanSpeedTest. Trinity reads the
    // device globals; on `done` it has already persisted wan_measured_limit and
    // cleaned up the on-device artifacts.
    getWanSpeedTestStatus: build.query<
      {
        success: boolean
        status: 'running' | 'done' | 'error'
        download_mbps?: number
        upload_mbps?: number
        measured_at?: string
        message?: string
      },
      { serial: string; runId: string }
    >({
      query: ({ serial, runId }) => ({
        method: 'get',
        url: `/${serial}/config/hardware/mikrotik/speed-test/status`,
        params: { run_id: runId },
      }),
    }),

    getNetworkOptimisation: build.query<
      NetworkOptimisationResponse,
      { serial: string }
    >({
      query: ({ serial }) => ({
        method: 'get',
        url: `/${serial}/config/hardware/mikrotik/optimise-network`,
      }),
      providesTags: (_0, _1, { serial }) => [
        { type: 'MikrotikNetworkRules', id: serial },
        { type: 'RadiusPolicyGroups', id: serial },
        { type: 'MikrotikHotspot', id: serial },
        { type: 'WanSettings', id: serial },
        { type: 'WanCapacity', id: serial },
      ],
    }),
    applyNetworkOptimisation: build.mutation<
      NetworkOptimisationResponse,
      { serial: string; queueBuild?: boolean }
    >({
      query: ({ serial, queueBuild = true }) => ({
        method: 'POST',
        url: `/${serial}/config/hardware/mikrotik/optimise-network/apply`,
        data: {
          queue_build: queueBuild,
        },
      }),
      invalidatesTags: (_0, _1, { serial }) => [
        { type: 'MikrotikNetworkRules', id: serial },
        { type: 'RadiusPolicyGroups', id: serial },
        { type: 'MikrotikHotspot', id: serial },
        { type: 'WanSettings', id: serial },
        { type: 'WanCapacity', id: serial },
        { type: 'MikrotikStatus', id: 'LIST' },
        { type: 'MikrotikStatus', id: `latest-build-${serial}` },
      ],
    }),

    // ── Device telemetry + live introspection ──────────────────────────────
    getDeviceTelemetry: build.query<
      DeviceTelemetrySampleType[],
      { serial: string; hours?: number }
    >({
      query: ({ serial, hours = 24 }) => ({
        method: 'get',
        url: `/${serial}/config/hardware/mikrotik/telemetry`,
        params: { hours },
      }),
      providesTags: (_0, _1, { serial }) => [
        { type: 'DeviceTelemetry', id: serial },
      ],
    }),
    getDeviceBandwidth: build.query<
      DeviceBandwidthSampleType[],
      { serial: string; hours?: number }
    >({
      query: ({ serial, hours = 24 }) => ({
        method: 'get',
        url: `/${serial}/config/hardware/mikrotik/telemetry/bandwidth`,
        params: { hours },
      }),
      providesTags: (_0, _1, { serial }) => [
        { type: 'DeviceBandwidth', id: serial },
      ],
    }),
    getDeviceBandwidthAggregate: build.query<
      DeviceBandwidthAggregateResponse,
      {
        serial: string
        tier?: DeviceBandwidthTierType
        networkId?: string
        apMac?: string
      }
    >({
      query: ({ serial, tier = 'network', networkId, apMac }) => ({
        method: 'get',
        url: `/${serial}/config/hardware/mikrotik/bandwidth`,
        params: {
          tier,
          network_id: networkId,
          ap_mac: apMac,
        },
      }),
      providesTags: (_0, _1, { serial }) => [
        { type: 'DeviceBandwidth', id: serial },
      ],
    }),
    getLiveResource: build.query<LiveResourceType, { serial: string }>({
      query: ({ serial }) => ({
        method: 'get',
        url: `/${serial}/config/hardware/mikrotik/live/resource`,
      }),
    }),
    getLiveQueues: build.query<LiveQueuesType, { serial: string }>({
      query: ({ serial }) => ({
        method: 'get',
        url: `/${serial}/config/hardware/mikrotik/live/queues`,
      }),
    }),
    getLiveApplicationQos: build.query<
      LiveApplicationQosType,
      { serial: string }
    >({
      query: ({ serial }) => ({
        method: 'get',
        url: `/${serial}/config/hardware/mikrotik/live/app-qos`,
      }),
      providesTags: (_0, _1, { serial }) => [
        { type: 'ApplicationQos', id: `${serial}:live` },
      ],
    }),
    getLiveHotspotActive: build.query<
      LiveHotspotActiveType,
      { serial: string }
    >({
      query: ({ serial }) => ({
        method: 'get',
        url: `/${serial}/config/hardware/mikrotik/live/hotspot/active`,
      }),
    }),
    scanNetwork: build.query<NetworkScanType, { serial: string }>({
      query: ({ serial }) => ({
        method: 'get',
        url: `/${serial}/config/hardware/mikrotik/live/scan`,
      }),
    }),
    getNetworkScan: build.query<
      NetworkScanResponseType,
      { serial: string; targetRange?: string }
    >({
      query: ({ serial, targetRange }) => ({
        method: 'get',
        url: `/${serial}/config/hardware/mikrotik/network-scan`,
        params: {
          ...(targetRange ? { target_range: targetRange } : {}),
        },
      }),
      providesTags: (_0, _1, { serial }) => [
        { type: 'MikrotikNetworkScan', id: serial },
      ],
    }),
    refreshNetworkScan: build.mutation<
      NetworkScanResponseType,
      {
        serial: string
        probeDefaultCreds?: boolean
        targetRange?: string
        targetInterface?: string
        confirm?: boolean
      }
    >({
      query: ({
        serial,
        probeDefaultCreds,
        targetRange,
        targetInterface,
        confirm,
      }) => ({
        method: 'POST',
        url: `/${serial}/config/hardware/mikrotik/network-scan/refresh`,
        params: {
          ...(probeDefaultCreds ? { probe_default_creds: 'true' } : {}),
          ...(targetRange ? { target_range: targetRange } : {}),
          ...(targetInterface ? { target_interface: targetInterface } : {}),
          ...(confirm ? { confirm: 'true' } : {}),
        },
      }),
      invalidatesTags: (_0, _1, { serial }) => [
        { type: 'MikrotikNetworkScan', id: serial },
      ],
    }),
    deployTelemetry: build.mutation<
      { success: boolean; queued: boolean },
      { serial: string }
    >({
      query: ({ serial }) => ({
        method: 'POST',
        url: `/${serial}/config/hardware/mikrotik/telemetry/deploy`,
      }),
      invalidatesTags: (_0, _1, { serial }) => [
        { type: 'DeviceTelemetry', id: serial },
        { type: 'DeviceBandwidth', id: serial },
      ],
    }),
    collectTelemetry: build.mutation<
      {
        success: boolean
        queued: boolean
        collected?: boolean
        reachable?: boolean
        queue_count?: number
        app_queue_count?: number
        interface_count?: number
      },
      { serial: string }
    >({
      query: ({ serial }) => ({
        method: 'POST',
        url: `/${serial}/config/hardware/mikrotik/telemetry/collect`,
      }),
      invalidatesTags: (_0, _1, { serial }) => [
        { type: 'DeviceTelemetry', id: serial },
        { type: 'DeviceBandwidth', id: serial },
      ],
    }),
    disconnectSession: build.mutation<
      { success: boolean; removed_active: number; note?: string },
      { serial: string; id: string }
    >({
      query: ({ serial, id }) => ({
        method: 'POST',
        url: `/${serial}/guests/sessions/${id}/disconnect`,
      }),
      invalidatesTags: [{ type: 'WiFiSessions', id: 'LIST' }],
    }),

    getConfigs: build.query<
      (WiFiConfigType & {
        inform: WiFiInformType
        snap_config?: SnapConfigType
        unifi_controller_list?: UniFiConfigType
        _count: {
          access_points: number
        }
      })[],
      { orgId: string; vendor_id?: string }
    >({
      query: ({ orgId, vendor_id }) => ({
        method: 'get',
        url: buildUrl(`/organisations/${orgId}/config`, { vendor_id }),
      }),
      providesTags: (_0, _1, { orgId }) => [
        { type: 'WiFiConfig' as const, id: 'LIST' },
      ],
    }),
    getAllUnifiControllers: build.query<
      UniFiControllerType[],
      { orgId: string }
    >({
      query: ({ orgId }) => ({
        method: 'get',
        url: `/organisations/${orgId}/unifi`,
      }),
      providesTags: (results, _1, { orgId }) => [
        ...(results ?? []).map(({ id }) => ({
          type: 'UniFiControllers' as const,
          id,
        })),
        { type: 'UniFiControllers' as const, id: 'LIST' },
      ],
    }),
    getUnifiLogCenter: build.query<
      UnifiLogCenterResponse,
      { orgId: string; serial?: string }
    >({
      query: ({ orgId, serial }) => ({
        method: 'get',
        url: buildUrl(`/organisations/${orgId}/unifi/log-center`, { serial }),
      }),
      providesTags: [{ type: 'UniFiControllers', id: 'LOG_CENTER' }],
    }),
    getUnifiLogCenterEvents: build.query<
      ServiceCursorPaginatedResponseType<UnifiLogCenterEvent>,
      {
        orgId: string
        serial?: string
        days?: number
        limit?: number
        cursor?: string
      }
    >({
      query: ({ orgId, serial, days, limit, cursor }) => ({
        method: 'get',
        url: buildUrl(`/organisations/${orgId}/unifi/log-center/events`, {
          serial,
          days,
          limit,
          cursor,
        }),
      }),
      providesTags: [{ type: 'UniFiControllers', id: 'LOG_CENTER_EVENTS' }],
    }),
    getUniFiControllerStatus: build.mutation<
      UniFiControllerType,
      { org_id: string; id: string }
    >({
      query: ({ org_id, id }) => ({
        method: 'get',
        url: `/organisations/${org_id}/unifi/${id}/health`,
      }),
      invalidatesTags: (_0, _1, { org_id, id }) => [
        { type: 'UniFiControllers', id },
        { type: 'WiFiConfig', id: 'LIST' },
        { type: 'UniFiControllerConfig', id: 'LIST' },
      ],
    }),
    deleteUniFiControllerSite: build.mutation<
      unknown,
      { org_id: string; id: string; site_id: string }
    >({
      query: ({ org_id, id, site_id }) => ({
        method: 'DELETE',
        url: `/organisations/${org_id}/unifi/${id}/sites/${site_id}`,
      }),
      invalidatesTags: (_0, _1, { org_id, id, site_id }) => [
        { type: 'UniFiControllers', id },
        { type: 'UniFiControllers', id: 'LIST' },
        { type: 'UniFiControllerConfig', id: site_id },
        { type: 'UniFiControllerConfig', id: 'LIST' },
      ],
    }),
    forceUniFiControllerVenueProvision: build.mutation<
      unknown,
      { org_id: string; id: string; site_id: string }
    >({
      query: ({ org_id, id, site_id }) => ({
        method: 'PUT',
        url: `/organisations/${org_id}/unifi/${id}/sites/${site_id}/force-provision`,
      }),
      invalidatesTags: (_0, _1, { org_id, id }) => [
        { type: 'UniFiControllers', id },
        { type: 'UniFiControllers', id: 'LIST' },
        { type: 'UniFiControllers', id: 'LOG_CENTER' },
        { type: 'UniFiControllerConfig', id: 'LIST' },
      ],
    }),
    getUnifiController: build.query<
      UniFiControllerType & { sites: [] },
      { org_id: string; id: string }
    >({
      query: ({ org_id, id }) => ({
        method: 'get',
        url: `/organisations/${org_id}/unifi/${id}`,
      }),
      providesTags: (_0, _1, { org_id, id }) => [
        { type: 'UniFiControllers', id },
      ],
    }),
    updateUnifiController: build.mutation<
      UniFiControllerType,
      {
        org_id: string
        id: string
        data: Pick<UniFiControllerType, 'hostname' | 'username' | 'password'>
      }
    >({
      query: ({ org_id, id, data }) => ({
        method: 'PUT',
        url: `/organisations/${org_id}/unifi/${id}`,
        data,
      }),
      invalidatesTags: (_0, _1, { org_id, id }) => [
        { type: 'UniFiControllers', id },
        { type: 'WiFiConfig', id: 'LIST' },
        { type: 'UniFiControllers', id: 'LIST' },
        { type: 'UniFiControllers', id: 'LOG_CENTER' },
        { type: 'UniFiControllerConfig', id: 'LIST' },
      ],
    }),
    createUnifiController: build.mutation<
      UniFiControllerType,
      {
        org_id: string
        data: Pick<UniFiControllerType, 'hostname' | 'username' | 'password'>
      }
    >({
      query: ({ org_id, data }) => ({
        method: 'POST',
        url: `/organisations/${org_id}/unifi`,
        data,
      }),
      invalidatesTags: (_0, _1, { org_id }) => [
        { type: 'UniFiControllers', id: 'LIST' },
        { type: 'WiFiConfig', id: 'LIST' },
        { type: 'UniFiControllerConfig', id: 'LIST' },
      ],
    }),
    deleteUnifiController: build.mutation<
      unknown,
      { org_id: string; id: string }
    >({
      query: ({ org_id, id }) => ({
        method: 'DELETE',
        url: `/organisations/${org_id}/unifi/${id}`,
      }),
      invalidatesTags: (_0, _1, { org_id }) => [
        { type: 'UniFiControllers', id: 'LIST' },
        { type: 'WiFiConfig', id: 'LIST' },
        { type: 'UniFiControllerConfig', id: 'LIST' },
      ],
    }),

    /**
     * MIKROTIK API END
     */

    /**
     * Network Rules API
     */
    getNetworkRules: build.query<
      SnapNetworkRuleType[],
      {
        serial: string
      }
    >({
      query: ({ serial }) => ({
        method: 'get',
        url: `/${serial}/config/hardware/mikrotik/network-rules`,
      }),
      providesTags: (_0, _1, { serial }) => [
        { type: 'MikrotikNetworkRules', id: serial },
      ],
    }),

    setNetworkRules: build.mutation<
      { success: boolean; id: string },
      {
        serial: string
        data: {
          name: string
          id?: string
          description?: string
          address: string
          gateway: string
          interface: string
          vlan_id?: number
          lease_time?: number
          bandwidth_limit?: string
          bandwidth_guaranteed?: string | null
          burst_limit?: string | null
          burst_threshold?: string | null
          burst_time?: string | null
          queue_priority?: number | null
          device_isolation?: boolean
          voice_provisioning?: boolean
          deployment_mode?: SnapNetworkRuleType['deployment_mode']
          template_type?: SnapNetworkRuleType['template_type']
          broadcast_ssid?: boolean
          ssid?: string
          ssid_password?: string
          dhcp_options?: DhcpNetworkOption[]
          /** Per-network AP controller adoption — DHCP inform (43/138). */
          controller_type?: 'unifi' | 'omada' | 'ruckus' | null
          controller_ip?: string | null
        }
      }
    >({
      query: ({ serial, data }) => {
        const description = data.description?.trim()
        const name = data.name.trim()

        return {
          method: 'POST',
          url: `/${serial}/config/hardware/mikrotik/network-rules`,
          data: {
            ...data,
            description: description || `${name || 'Network'} configuration`,
          },
        }
      },
      // Network guarantees count against WAN capacity, so refresh it too.
      invalidatesTags: (_0, _1, { serial }) => [
        { type: 'MikrotikNetworkRules', id: serial },
        { type: 'WanCapacity', id: serial },
      ],
    }),

    deleteNetworkRule: build.mutation<
      { success: boolean },
      {
        serial: string
        id: string
      }
    >({
      query: ({ serial, id }) => ({
        method: 'DELETE',
        url: `/${serial}/config/hardware/mikrotik/network-rules/${id}`,
      }),
      invalidatesTags: (_0, _1, { serial }) => [
        { type: 'MikrotikNetworkRules', id: serial },
      ],
    }),

    /**
     * Firewall Rules API
     */
    getFirewallRules: build.query<
      SnapFirewallRuleType[],
      {
        serial: string
      }
    >({
      query: ({ serial }) => ({
        method: 'get',
        url: `/${serial}/config/hardware/mikrotik/firewall-rules`,
      }),
      providesTags: (_0, _1, { serial }) => [
        { type: 'MikrotikFirewallRules', id: serial },
      ],
    }),

    setFirewallRules: build.mutation<
      { success: boolean },
      {
        serial: string
        data: {
          name: string
          description?: string
          address?: string
          rule_type: string
          list_name?: string
          chain?: string
          action?: string
          protocol?: string
          port?: string
          dst_port?: string
          to_addresses?: string
          to_ports?: string
          disabled?: boolean
          is_hostname?: boolean
        }
      }
    >({
      query: ({ serial, data }) => ({
        method: 'POST',
        url: `/${serial}/config/hardware/mikrotik/firewall-rules`,
        data,
      }),
      invalidatesTags: (_0, _1, { serial }) => [
        { type: 'MikrotikFirewallRules', id: serial },
      ],
    }),

    deleteFirewallRule: build.mutation<
      { success: boolean },
      {
        serial: string
        id: string
      }
    >({
      query: ({ serial, id }) => ({
        method: 'DELETE',
        url: `/${serial}/config/hardware/mikrotik/firewall-rules/${id}`,
      }),
      invalidatesTags: (_0, _1, { serial }) => [
        { type: 'MikrotikFirewallRules', id: serial },
      ],
    }),

    syncFirewallRules: build.mutation<{ success: boolean }, { serial: string }>(
      {
        query: ({ serial }) => ({
          method: 'POST',
          url: `/${serial}/config/hardware/mikrotik/firewall-rules/sync`,
        }),
        invalidatesTags: (_0, _1, { serial }) => [
          { type: 'MikrotikFirewallRules', id: serial },
        ],
      }
    ),

    // Hotspot Rules endpoints
    getHotspotRules: build.query<
      {
        address_pool: string
        addresses_per_mac: number
        completed_at: string
        created_at: string
        description: string
        disabled: boolean
        dns_name: string
        errored_at: string
        hotspot_address: string
        id: string
        idle_timeout: string
        interface: string
        keepalive_timeout: string
        login_by: string
        login_timeout: string
        mac_auth_enabled: boolean
        mac_auth_mode: string
        mikrotik_hotspot_id: string
        mikrotik_profile_id: string
        mikrotik_user_profile_id: string
        name: string
        network_rule_id: string
        profile: string
        radius_accounting: boolean
        radius_interim_update: string
        radius_location_id: string
        radius_mac_format: string
        rate_limit: string
        session_timeout: string
        shared_users: number
        snap_config_id: string
        transparent_proxy: boolean
        updated_at: string
        use_radius: boolean
        user_profile_name: string
      }[],
      { serial: string }
    >({
      query: ({ serial }) => ({
        url: `/${serial}/config/hardware/mikrotik/hotspot-rules`,
        method: 'get',
      }),
    }),
    setHotspotRules: build.mutation<any, { serial: string; data: any }>({
      query: ({ serial, data }) => ({
        url: `/${serial}/config/hardware/mikrotik/hotspot-rules`,
        method: 'POST',
        data,
      }),
    }),
    deleteHotspotRule: build.mutation<any, { serial: string; id: string }>({
      query: ({ serial, id }) => ({
        url: `/${serial}/config/hardware/mikrotik/hotspot-rules/${id}`,
        method: 'DELETE',
      }),
    }),

    getDhcpLeaseFilters: build.query<
      {
        networks: {
          id: string
          name: string
          vlan_id: number
          ssid: string
          interface: string
        }[]
        vendors: { mac_prefix: string; name: string }[]
        access_points: {
          mac: string
          name: string
          model: string
          is_online: boolean
        }[]
        device_types: string[]
        device_brands: string[]
        browsers: string[]
        operating_systems: { name: string; platform: string }[]
        ssids: string[]
        vlan_ids: number[]
      },
      { serial: string }
    >({
      query: ({ serial }) => ({
        url: `/${serial}/radius/dhcp-log/filters`,
        method: 'get',
      }),
    }),

    getDhcpLeases: build.query<
      any,
      {
        serial: string
        cursor?: string | number
        limit: number
        sort_by?: 'asc' | 'desc'
        network?: string
        networks?: string
        ip?: string
        ips?: string
        mac?: string
        macs?: string
        access_point?: string
        port_id?: string
        vendor_id?: string
        device_type?: string
        device_brand?: string
        mobile?: string
        email?: string
        name?: string
        has_session?: string
        vlan_id?: string
        ssid?: string
        search?: string
        start?: string
        end?: string
      }
    >({
      query: ({ serial, ...params }) => ({
        url: `/${serial}/radius/dhcp-log`,
        method: 'get',
        params,
      }),
    }),

    /**
     * Snap Provisioning API
     */
    createSnapDevice: build.mutation<
      { success: true; data: SnapProvisioningDevice },
      {
        device_serial: string
        device_mac: string
        device_board_name: string
        device_model: string
        device_firmware_version: string
        wan_ip: string
        lan_ip: string
        device_password?: string
        config_id?: string
        ownership_organisation_id?: string
        status?: 'PENDING' | 'ACTIVE' | 'INACTIVE'
      }
    >({
      query: (data) => ({
        url: '/api/snap-provisioning',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['SnapDevice', 'WiFiConfig'],
    }),

    getSnapDevice: build.query<
      { success: true; data: SnapProvisioningDevice },
      { device_serial: string }
    >({
      query: ({ device_serial }) => ({
        url: `/api/snap-provisioning/${device_serial}`,
        method: 'GET',
      }),
      providesTags: (_0, _1, { device_serial }) => [
        { type: 'SnapDevice', id: device_serial },
      ],
    }),

    listSnapDevices: build.query<
      {
        success: true
        data: {
          devices: SnapProvisioningDevice[]
          pagination: {
            page: number
            limit: number
            total: number
            total_pages: number
          }
        }
      },
      {
        status?: 'PENDING' | 'ACTIVE' | 'INACTIVE'
        vpn_connected?: boolean
        config_id?: string
        ownership_organisation_id?: string
        page?: number
        limit?: number
      }
    >({
      query: (params) => ({
        url: '/api/snap-provisioning',
        method: 'GET',
        params,
      }),
      providesTags: ['SnapDevice'],
    }),

    updateSnapDevice: build.mutation<
      { success: true; data: SnapProvisioningDevice; message?: string },
      {
        device_serial: string
        data: Partial<{
          device_mac: string
          device_board_name: string
          device_model: string
          device_firmware_version: string
          wan_ip: string
          lan_ip: string
          device_password: string
          ownership_organisation_id: string | null
          config_id: string | null
          status: 'PENDING' | 'ACTIVE' | 'INACTIVE'
          vpn_connected: boolean
          edge_runtime_status:
            | 'installing'
            | 'running'
            | 'failed'
            | 'uninstalled'
            | null
          edge_runtime_image_tag: string | null
        }>
      }
    >({
      query: ({ device_serial, data }) => ({
        url: `/api/snap-provisioning/${device_serial}`,
        method: 'PATCH',
        data,
      }),
      invalidatesTags: (_0, _1, { device_serial }) => [
        { type: 'SnapDevice', id: device_serial },
        { type: 'SnapDevice', id: 'LIST' },
      ],
    }),

    linkSnapDeviceToConfig: build.mutation<
      {
        success: true
        message: string
        data: SnapProvisioningDevice
      },
      {
        device_serial: string
        config_id?: string | null
        ownership_organisation_id?: string | null
        device_password?: string
        device_mac?: string
        device_board_name?: string
        device_model?: string
        device_firmware_version?: string
        wan_ip?: string
        lan_ip?: string
        status?: 'PENDING' | 'ACTIVE' | 'INACTIVE'
        vpn_connected?: boolean
      }
    >({
      query: ({ device_serial, ...data }) => ({
        url: `/api/snap-provisioning/${device_serial}/link`,
        method: 'POST',
        data,
      }),
      invalidatesTags: (_0, _1, { device_serial }) => [
        { type: 'SnapDevice', id: device_serial },
        { type: 'SnapDevice', id: 'LIST' },
        'WiFiConfig',
        'WiFiInform',
        'MikrotikStatus',
      ],
    }),

    unlinkSnapDeviceFromConfig: build.mutation<
      {
        success: true
        message?: string
        data: SnapProvisioningDevice
      },
      { device_serial: string }
    >({
      query: ({ device_serial }) => ({
        url: `/api/snap-provisioning/${device_serial}`,
        method: 'PATCH',
        data: { config_id: null },
      }),
      invalidatesTags: (_0, _1, { device_serial }) => [
        { type: 'SnapDevice', id: device_serial },
        { type: 'SnapDevice', id: 'LIST' },
        'WiFiConfig',
        'WiFiInform',
      ],
    }),

    deleteSnapDevice: build.mutation<
      {
        success: true
        message: string
        data: {
          device_serial: string
          was_linked_to: string | null
        }
      },
      { device_serial: string }
    >({
      query: ({ device_serial }) => ({
        url: `/api/snap-provisioning/${device_serial}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['SnapDevice', 'WiFiConfig'],
    }),

    listUnlinkedSnapDevices: build.query<
      {
        success: true
        data: {
          devices: SnapProvisioningDevice[]
          pagination: {
            page: number
            limit: number
            total: number
            total_pages: number
          }
        }
      },
      { page?: number; limit?: number }
    >({
      query: (params) => ({
        url: '/api/snap-provisioning/unlinked',
        method: 'GET',
        params,
      }),
      providesTags: ['SnapDevice'],
    }),

    /**
     * Provisioning Builds API
     */
    getProvisioningBuilds: build.query<
      {
        success: true
        builds: {
          id: string
          status: 'PENDING' | 'RUNNING' | 'SUCCESS' | 'FAILED'
          created_at: string
          started_at: string | null
          completed_at: string | null
          script_size_bytes: number
          script_hash: string
          error_message: string | null
          error_line: number | null
          error_column: number | null
          triggered_by_action: string
          triggered_by_user: string | null
          connection_method: 'API' | 'VPN_PROXY' | 'SSH' | null
          device_version: string | null
          is_vpn: boolean
          has_ssl: boolean
        }[]
        total: number
      },
      { serial: string }
    >({
      query: ({ serial }) => ({
        url: `/${serial}/config/hardware/mikrotik/builds`,
        method: 'GET',
      }),
      providesTags: (_0, _1, { serial }) => [
        { type: 'MikrotikStatus', id: 'LIST' },
      ],
    }),

    getLatestProvisioningBuild: build.query<
      {
        success: true
        build: {
          id: string
          build_id: string
          device_serial: string
          status: 'pending' | 'in_progress' | 'completed' | 'failed'
          progress: number
          current_step: string | null
          total_steps: number
          completed_steps: number
          error: string | null
          started_at: string
          completed_at: string | null
          created_at: string
          script_size_bytes: number
          script_hash: string
          error_message: string | null
          error_line: number | null
          error_column: number | null
          triggered_by_action: string
          triggered_by_user: string | null
          connection_method: 'API' | 'VPN_PROXY' | 'SSH' | null
          device_version: string | null
          is_vpn: boolean
          has_ssl: boolean
          steps: {
            name: string
            status: 'pending' | 'in_progress' | 'completed' | 'failed'
            timestamp: string | null
          }[]
          /**
           * Stage checklist + device console for the build activity panel.
           * `stages` is the ordered section list parsed from the build
           * script's beacons; `stage_current` is 1-based; `console` is the
           * device's script-topic log tail, cached during (and shortly
           * after) the run.
           */
          stages?: string[]
          stage_current?: number | null
          stage_total?: number | null
          stage_label?: string | null
          console?: { time: string; message: string }[]
        }
      },
      { serial: string }
    >({
      query: ({ serial }) => ({
        url: `/${serial}/config/hardware/mikrotik/builds/latest`,
        method: 'GET',
      }),
      providesTags: (_0, _1, { serial }) => [
        { type: 'MikrotikStatus', id: `latest-build-${serial}` },
      ],
    }),

    getProvisioningBuildStatus: build.query<
      {
        success: true
        build: {
          id: string
          status: 'PENDING' | 'RUNNING' | 'SUCCESS' | 'FAILED'
          error_message: string | null
          created_at: string
          started_at: string | null
          completed_at: string | null
          duration_ms: number | null
          script_hash: string
          script_size_bytes: number
        }
        queue: {
          position: number | null
          status: string | null
          job_id: string | null
        }
      },
      { serial: string; buildId: string }
    >({
      query: ({ serial, buildId }) => ({
        url: `/${serial}/config/hardware/mikrotik/builds/${buildId}/status`,
        method: 'GET',
      }),
      providesTags: (_0, _1, { serial, buildId }) => [
        { type: 'MikrotikStatus', id: `build-status-${serial}-${buildId}` },
      ],
    }),

    createProvisioningBuild: build.mutation<
      { success: true; build_id: string },
      {
        serial: string
        hostname: string
        username: string
        password: string
        oauth_client_hostname: string
        oauth_client_id: string
        oauth_client_secret: string
      }
    >({
      query: ({
        serial,
        hostname,
        username,
        password,
        oauth_client_hostname,
        oauth_client_id,
        oauth_client_secret,
      }) => ({
        url: `/${serial}/config/hardware/mikrotik/builds`,
        method: 'POST',
        data: {
          hostname,
          username,
          password,
          oauth_client_hostname,
          oauth_client_id,
          oauth_client_secret,
        },
      }),
      invalidatesTags: (_0, _1, { serial }) => [
        { type: 'MikrotikStatus', id: 'LIST' },
      ],
    }),

    validateBuildConfiguration: build.mutation<
      {
        success: boolean
        validation: {
          summary: {
            passed: number
            totalChecks: number
            failed: number
          }
          checks: {
            name: string
            passed: boolean
            message?: string
            details?: Record<string, unknown>
          }[]
        }
      },
      {
        serial: string
        export_file_path: string
      }
    >({
      query: ({ serial, export_file_path }) => ({
        url: `/${serial}/config/hardware/mikrotik/builds/validate`,
        method: 'POST',
        data: {
          export_file_path,
        },
      }),
    }),

    getProvisioningBuildDetails: build.query<
      {
        success: true
        build: {
          id: string
          status: 'PENDING' | 'RUNNING' | 'SUCCESS' | 'FAILED'
          created_at: string
          started_at: string | null
          completed_at: string | null
          script_size_bytes: number
          script_hash: string
          script_content: string | null
          execution_output: string | null
          error_message: string | null
          error_line: number | null
          error_column: number | null
          triggered_by_action: string
          triggered_by_user: string | null
          connection_method: 'API' | 'VPN_PROXY' | 'SSH' | null
          device_version: string | null
          is_vpn: boolean
          has_ssl: boolean
          duration_ms: number | null
          duration_seconds: number | null
          script_lines: number
          script_commands: number
          script_has_comments: boolean
          script_changed_from_previous: boolean
        }
        related_builds: {
          previous: {
            id: string
            status: string
            created_at: string
            script_hash: string
          } | null
          next: {
            id: string
            status: string
            created_at: string
            script_hash: string
          } | null
          duplicates: {
            id: string
            status: string
            created_at: string
            triggered_by_action: string
          }[]
          duplicate_count: number
        }
        statistics: {
          total_builds: number
          successful_builds: number
          failed_builds: number
          success_rate: number
        }
        device_info: {
          serial: string
          board_name: string
          version: string
          last_connection_method: string
          api_port_open: boolean
          ssl_port_open: boolean
          vpn_is_online: boolean
        }
      },
      { serial: string; buildId: string }
    >({
      query: ({ serial, buildId }) => ({
        url: `/${serial}/config/hardware/mikrotik/builds/${buildId}/details`,
        method: 'GET',
      }),
      providesTags: (_0, _1, { serial, buildId }) => [
        { type: 'MikrotikStatus', id: `build-${serial}-${buildId}` },
      ],
    }),

    rerunProvisioningBuild: build.mutation<
      { success: true; build_id: string },
      { serial: string; buildId: string }
    >({
      query: ({ serial, buildId }) => ({
        url: `/${serial}/config/hardware/mikrotik/builds/${buildId}/rerun`,
        method: 'POST',
      }),
      invalidatesTags: (_0, _1, { serial, buildId }) => [
        { type: 'MikrotikStatus', id: 'LIST' },
        { type: 'MikrotikStatus', id: `build-${serial}-${buildId}` },
      ],
    }),

    deleteProvisioningBuild: build.mutation<
      { success: true },
      { serial: string; buildId: string }
    >({
      query: ({ serial, buildId }) => ({
        url: `/${serial}/config/hardware/mikrotik/builds/${buildId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_0, _1, { serial, buildId }) => [
        { type: 'MikrotikStatus', id: 'LIST' },
        { type: 'MikrotikStatus', id: `build-${serial}-${buildId}` },
      ],
    }),

    /**
     * Generate Provisioning Script
     */
    generateProvisioningScript: build.mutation<Blob, { serial: string }>({
      queryFn: async ({ serial }, { signal }) => {
        try {
          const response = await customAxios({
            url: `${config.url.trinity}/${serial}/config/hardware/mikrotik/generate-provision-script`,
            method: 'GET',
            responseType: 'blob',
            signal,
          })

          return { data: response.data }
        } catch (error) {
          if (axios.isAxiosError(error)) {
            return {
              error: {
                status: error.response?.status,
                data: error.response?.data || error.message,
              },
            }
          }
          return { error: { status: 500, data: 'Unknown error' } }
        }
      },
    }),

    /**
     * Identify Device (Modem Handshake Sound)
     */
    playModemHandshake: build.mutation<
      { success: boolean },
      { serial: string }
    >({
      query: ({ serial }) => ({
        url: `/${serial}/config/hardware/mikrotik/modem-handshake`,
        method: 'POST',
      }),
    }),

    /**
     * Auto-Provision Device (Snap Guard)
     */
    autoProvisionDevice: build.mutation<
      {
        success: boolean
        message: string
        serial: string
        buildId: string
        jobId: string
        status: 'QUEUED' | 'PENDING' | 'RUNNING' | 'SUCCESS' | 'FAILED'
      },
      {
        serial: string
        server_hostname: string
        mode: 'admin' | 'org' | 'venue'
        org_id?: string
        venue_serial?: string
      }
    >({
      query: ({ serial, server_hostname, mode, org_id, venue_serial }) => ({
        url: '/snap/auto-provision',
        method: 'POST',
        data: {
          serial,
          server_hostname,
          mode,
          ...(org_id && { org_id }),
          ...(venue_serial && { venue_serial }),
        },
      }),
      invalidatesTags: ['SnapDevice', 'WiFiConfig'],
    }),

    /**
     * Get Build Status
     */
    getBuildStatus: build.query<
      {
        success: boolean
        build: {
          id: string
          serial: string
          status: 'PENDING' | 'RUNNING' | 'SUCCESS' | 'FAILED'
          script?: string
          output?: string
          error?: string
          errorLine?: number
          errorColumn?: number
          scriptHash?: string
          createdAt: string
          updatedAt: string
          startedAt?: string
          completedAt?: string
        }
      },
      { serial: string; buildId: string }
    >({
      query: ({ serial, buildId }) => ({
        url: `/${serial}/config/hardware/mikrotik/builds/${buildId}/status`,
        method: 'GET',
      }),
      providesTags: (result, error, { buildId }) => [
        { type: 'MikrotikStatus', id: `build-${buildId}` },
      ],
    }),

    /**
     * Run Connectivity Test
     */
    runConnectivityTest: build.mutation<
      {
        success: boolean
        target: string
        data: {
          ping: {
            success: boolean
            avg_rtt?: number
            packet_loss?: number
            error?: string
          }
          dns: {
            success: boolean
            resolution_time?: number
            resolved_ip?: string
            error?: string
          }
          gateway: {
            reachable: boolean
            gateway_ip?: string
            error?: string
          }
        }
      },
      { serial: string; target?: string }
    >({
      query: ({ serial, target }) => ({
        url: `/${serial}/config/hardware/mikrotik/connectivity-test${target ? `?target=${encodeURIComponent(target)}` : ''}`,
        method: 'GET',
      }),
    }),

    /**
     * Get Uplink Information
     */
    getUplinkInfo: build.query<
      {
        success: boolean
        data: {
          defaultRoute: {
            dst: string
            gateway: string
            routingTable: string
            distance: number
          }
          uplink: {
            interface: string
            type: string
            disabled: boolean
            traffic: {
              rx_bps: number
              tx_bps: number
              rx_human: string
              tx_human: string
            }
            link: {
              running: boolean
              rate: string
              fullDuplex: boolean
              autoNegotiation: boolean
              advertising?: string
              linkPartnerAdvertising?: string
            }
            ip_addresses: {
              address: string
              network: string
              actual_interface: string
              disabled: boolean
              dynamic: boolean
            }[]
            dhcp_client: {
              status: string
              address: string
              gateway: string
              dhcp_server: string
              primary_dns: string
              expires_after: string
              use_peer_dns: boolean
              use_peer_ntp: boolean
              add_default_route: boolean
            }
            statistics: {
              rx_bytes: number
              tx_bytes: number
              rx_packets: number
              tx_packets: number
              running: boolean
              last_link_up_time: string
            }
          }
        }
        timestamp: string
      },
      { serial: string }
    >({
      query: ({ serial }) => ({
        url: `/${serial}/config/hardware/mikrotik/uplink-info`,
        method: 'GET',
      }),
    }),

    /**
     * TR069 API Endpoints
     */
    getTR069Device: build.query<
      {
        success: boolean
        device: {
          serial: string
          manufacturer: string
          model: string
          firmware_version: string
          hardware_version: string
          connection_request_url: string
          last_inform: string
          last_boot: string
          uptime: number
          status: 'online' | 'offline' | 'unknown'
          parameters: {
            name: string
            value: string
            writable: boolean
            type: string
          }[]
          pending_tasks: {
            id: string
            type: string
            status: 'pending' | 'in_progress' | 'completed' | 'failed'
            created_at: string
          }[]
        }
      },
      { serial: string }
    >({
      queryFn: async ({ serial }) => {
        try {
          const response = await customAxios({
            url: `${config.url.vpn}/api/tr069/device/${serial}`,
            method: 'GET',
          })
          return { data: response.data }
        } catch (error) {
          if (axios.isAxiosError(error)) {
            return {
              error: {
                status: error.response?.status,
                data: error.response?.data || error.message,
              },
            }
          }
          return { error: { status: 500, data: 'Unknown error' } }
        }
      },
      providesTags: (_0, _1, { serial }) => [
        { type: 'MikrotikTR069', id: serial },
      ],
    }),

    getTR069Dashboard: build.query<
      {
        success: boolean
        total_devices: number
        online_devices: number
        offline_devices: number
        devices: {
          serial: string
          manufacturer: string
          model: string
          status: 'online' | 'offline' | 'unknown'
          last_inform: string
          firmware_version: string
        }[]
      },
      void
    >({
      queryFn: async () => {
        try {
          const response = await customAxios({
            url: `${config.url.vpn}/api/tr069/dashboard`,
            method: 'GET',
          })
          return { data: response.data }
        } catch (error) {
          if (axios.isAxiosError(error)) {
            return {
              error: {
                status: error.response?.status,
                data: error.response?.data || error.message,
              },
            }
          }
          return { error: { status: 500, data: 'Unknown error' } }
        }
      },
      providesTags: ['MikrotikTR069'],
    }),

    /**
     * SNMP API Endpoints
     */
    /**
     * Get Activity Feed for Venue
     * Unified feed combining health events, sessions, snap builds, snap events, and IP updates
     */
    getVenueActivityFeed: build.query<
      ServiceCursorPaginatedResponseType<{
        id: string
        type: 'health' | 'session' | 'snap_build' | 'snap_event' | 'ip_update'
        timestamp: string
        created_at: string
        updated_at?: string | null
        // Health event fields
        health_event?: {
          id: string
          inform_id: string
          ip: string
          serial: string
          status: string
          config: WiFiConfigType
        }
        // Session fields
        session?: {
          id: string
          download: number
          upload: number
          ended_at: string | null
          config: WiFiConfigType
        }
        // Snap build fields
        snap_build?: {
          id: string
          status: string
          script_size_bytes: number
          error_message: string | null
          device_serial: string
          config: WiFiConfigType
        }
        // Snap event fields
        snap_event?: {
          event_type: string
          event_data: any
          device_serial: string
          config: WiFiConfigType
        }
        // IP update fields (from health events)
        ip_update?: {
          old_ip?: string
          new_ip: string
          serial: string
          config: WiFiConfigType
        }
      }>,
      {
        serial: string
        limit?: number
        cursor?: string
        types?: (
          | 'health'
          | 'session'
          | 'snap_build'
          | 'snap_event'
          | 'ip_update'
        )[]
      }
    >({
      query: ({ serial, limit = 50, cursor, types }) => ({
        url: buildUrl('/admin/health/activity-feed', {
          serial,
          limit,
          cursor,
          types: types?.join(','),
        }),
        method: 'GET',
      }),
      providesTags: (result, error, { serial }) => [
        { type: 'WiFiConfig', id: serial },
      ],
    }),

    // ── SNAPguard Onboarding Progress ──

    getSnapOnboardingProgress: build.query<
      SnapOnboardingProgress,
      { serial: string }
    >({
      query: ({ serial }) => ({
        url: `/${serial}/onboarding/progress`,
        method: 'GET',
      }),
      providesTags: (_r, _e, { serial }) => [
        { type: 'SnapOnboarding', id: serial },
      ],
    }),

    updateSnapOnboardingProgress: build.mutation<
      SnapOnboardingProgress,
      {
        serial: string
        data: {
          current_step?: string
          step_completed?: string
          step_uncompleted?: string
        }
      }
    >({
      query: ({ serial, data }) => ({
        url: `/${serial}/onboarding/progress`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: (_r, _e, { serial }) => [
        { type: 'SnapOnboarding', id: serial },
      ],
    }),

    validateSnapOnboarding: build.query<
      {
        steps: Record<string, { complete: boolean; issues: string[] }>
        can_go_live: boolean
        required_incomplete: string[]
      },
      { serial: string }
    >({
      query: ({ serial }) => ({
        url: `/${serial}/onboarding/validate`,
        method: 'POST',
      }),
    }),

    completeSnapOnboarding: build.mutation<
      { progress: SnapOnboardingProgress; activate_portal: boolean },
      { serial: string; data: { activate_portal: boolean } }
    >({
      query: ({ serial, data }) => ({
        url: `/${serial}/onboarding/complete`,
        method: 'POST',
        data,
      }),
      invalidatesTags: (_r, _e, { serial }) => [
        { type: 'SnapOnboarding', id: serial },
      ],
    }),

    dismissSnapOnboarding: build.mutation<
      SnapOnboardingProgress,
      { serial: string }
    >({
      query: ({ serial }) => ({
        url: `/${serial}/onboarding/dismiss`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, { serial }) => [
        { type: 'SnapOnboarding', id: serial },
      ],
    }),

    resetSnapOnboarding: build.mutation<
      SnapOnboardingProgress,
      { serial: string }
    >({
      query: ({ serial }) => ({
        url: `/${serial}/onboarding/reset`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, { serial }) => [
        { type: 'SnapOnboarding', id: serial },
      ],
    }),

    // ── Customer hardware (org-scoped Snap devices) ──

    /**
     * The customer-facing list of hardware boxes owned by an org. Org scoping
     * is enforced server-side via auth + org middleware; the response never
     * includes device passwords.
     */
    listOrgHardwareDevices: build.query<
      { success: true; data: OrgHardwareDevice[] },
      { org_id: string }
    >({
      query: ({ org_id }) => ({
        url: `/organisations/${org_id}/snap/devices`,
        method: 'GET',
      }),
      providesTags: (res) =>
        res?.data
          ? [
              ...res.data.map((d) => ({
                type: 'SnapDevice' as const,
                id: d.device_serial,
              })),
              { type: 'SnapDevice' as const, id: 'LIST' },
            ]
          : [{ type: 'SnapDevice' as const, id: 'LIST' }],
    }),

    /**
     * Live resilient-pair health for a venue-linked box: trinity probes both
     * nodes over their SnapGuard tunnels (VRRP state, VPN visibility) and
     * evaluates the pair. `profile: null` when the venue has no resilience.
     */
    getOrgSnapResilience: build.query<
      {
        success: boolean
        profile: OrgResilienceProfile | null
        health: OrgResilienceHealth
      },
      { org_id: string; device_serial: string }
    >({
      query: ({ org_id, device_serial }) => ({
        url: `/organisations/${org_id}/snap/devices/${device_serial}/resilience`,
        method: 'GET',
      }),
      providesTags: (_r, _e, { device_serial }) => [
        { type: 'SnapDevice', id: device_serial },
      ],
    }),

    getOrgSnapResilienceOptions: build.query<
      OrgResilienceSetupOptions,
      {
        org_id: string
        device_serial: string
        candidate_serial?: string
      }
    >({
      query: ({ org_id, device_serial, candidate_serial }) => ({
        url: `/organisations/${org_id}/snap/devices/${device_serial}/resilience/options`,
        method: 'GET',
        params: candidate_serial ? { candidate_serial } : undefined,
      }),
      providesTags: (_r, _e, { device_serial }) => [
        { type: 'SnapDevice', id: device_serial },
      ],
    }),

    setOrgSnapResilience: build.mutation<
      {
        success: boolean
        profile: OrgResilienceProfile
        reprovision_required: boolean
        vpn_mirrored: boolean
      },
      {
        org_id: string
        device_serial: string
        profile: OrgResilienceProfile
        move_linked_standby?: boolean
      }
    >({
      query: ({ org_id, device_serial, profile, move_linked_standby }) => ({
        url: `/organisations/${org_id}/snap/devices/${device_serial}/resilience`,
        method: 'PUT',
        data: {
          ...profile,
          ...(move_linked_standby ? { move_linked_standby: true } : {}),
        },
      }),
      invalidatesTags: (_r, _e, { device_serial }) => [
        { type: 'SnapDevice', id: device_serial },
        { type: 'SnapDevice', id: 'LIST' },
      ],
    }),

    /**
     * Activate a box the customer has just plugged in, using the password
     * printed on the back. Error contract (surfaced via the RTK error):
     *  - 400 `{ error: 'INVALID_PASSWORD', attempts_remaining }`
     *  - 403 `{ error: 'DEVICE_NOT_AVAILABLE' }`
     *  - 423 `{ error: 'LOCKED', retry_after_seconds }`
     *  - 404 not found
     */
    activateSnapDevice: build.mutation<
      { success: true; data: OrgHardwareDevice },
      { org_id: string; device_serial: string; device_password: string }
    >({
      query: ({ org_id, device_serial, device_password }) => ({
        url: `/organisations/${org_id}/snap/devices/${device_serial}/activate`,
        method: 'POST',
        data: { device_password },
      }),
      invalidatesTags: (_r, _e, { device_serial }) => [
        { type: 'SnapDevice', id: device_serial },
        { type: 'SnapDevice', id: 'LIST' },
      ],
    }),

    /**
     * Unlink a box from its venue while keeping its stored password, VPN
     * registration and org ownership, so it can be set up at another venue
     * without re-enrolment. 409 `{ error: 'RESILIENT_PAIR_MEMBER' }` while
     * the box still has a resilient role — remove the pairing first.
     */
    unlinkSnapDevice: build.mutation<
      { success: true },
      { org_id: string; device_serial: string }
    >({
      query: ({ org_id, device_serial }) => ({
        url: `/organisations/${org_id}/snap/devices/${device_serial}/unlink`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, { device_serial }) => [
        { type: 'SnapDevice', id: device_serial },
        { type: 'SnapDevice', id: 'LIST' },
      ],
    }),

    /**
     * Post-enrol health checks for a box, used by the "Go live" wizard step to
     * poll for a healthy device after provisioning. Trinity returns three raw
     * port/login booleans; healthy = the router accepts API connections and
     * our credentials work (the SSL challenge port only matters for
     * certificate issuance, so it's reported but not gating).
     */
    getSnapEnrollChecks: build.query<
      {
        healthy: boolean
        checks: {
          name: string
          passed: boolean
          message?: string
        }[]
      },
      { serial: string }
    >({
      query: ({ serial }) => ({
        url: `/snap/enroll/${serial}/checks`,
        method: 'GET',
      }),
      transformResponse: (res: {
        is_ssl_challenge_port_open: boolean
        is_api_port_open: boolean
        is_login_successful: boolean
      }) => ({
        healthy: res.is_api_port_open && res.is_login_successful,
        checks: [
          {
            name: 'Box reachable',
            passed: res.is_api_port_open,
            message: res.is_api_port_open
              ? undefined
              : 'Waiting for the box to accept connections',
          },
          {
            name: 'Credentials verified',
            passed: res.is_login_successful,
            message: res.is_login_successful
              ? undefined
              : 'Verifying the box credentials',
          },
          {
            name: 'Certificate port open',
            passed: res.is_ssl_challenge_port_open,
            message: res.is_ssl_challenge_port_open
              ? undefined
              : 'Waiting for the certificate port to open',
          },
        ],
      }),
      providesTags: (_r, _e, { serial }) => [
        { type: 'SnapDevice', id: serial },
      ],
    }),
  }),
})

export default wifi
